// App.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase"; // Adjust path if needed
import { Modal, Button, Form, Dropdown, DropdownButton } from "react-bootstrap";
import { ChordProParser, HtmlDivFormatter, TextFormatter, Key } from "chordsheetjs";
import "./App.css";

const MIX_STORAGE_KEY = "ct-mix-settings";
const CLICK_PRESET_STORAGE_KEY = "ct-click-preset";
const PAD_OVERRIDE_PREFIX = "ct-pad-override";
const SELECTED_BAND_STORAGE_PREFIX = "ct-selected-band";
const SHARP_TO_FLAT = {
  "C#": "Db",
  "D#": "Eb",
  "F#": "Gb",
  "G#": "Ab",
  "A#": "Bb",
};
const normalizePadKey = (key) => {
  if (!key) return "C";
  const match = String(key)
    .trim()
    .match(/^([A-Ga-g])([#b]?)(m|min)?$/i);
  if (!match) return "C";
  const letter = match[1].toUpperCase();
  const accidental = match[2] === "#" ? "#" : match[2] === "b" ? "b" : "";
  const isMinor = Boolean(match[3]);
  const normalized = SHARP_TO_FLAT[`${letter}${accidental}`] || `${letter}${accidental}`;
  const root = normalized || "C";
  return `${root}${isMinor ? "m" : ""}`;
};
const PAD_MAJOR_KEYS = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];
const PAD_MINOR_KEYS = ["Am", "A#m", "Bbm", "Bm", "Cm", "C#m", "Dbm", "Dm", "D#m", "Ebm", "Em", "Fm", "F#m", "Gbm", "Gm", "G#m", "Abm"];
const PRACTICE_STORAGE_KEY = "ct-practice-settings";
const PRACTICE_DEFAULTS = {
  tempo: 72,
  beatsPerMeasure: 4,
  suppressDownbeat: false,
  padKey: "C",
  padVolume: 1,
  clickVolume: 1,
};
const getYouTubeEmbedId = (song) => song?.track || song?.url || null;
const hasOnlineYouTube = (song) => Boolean(song?.url && getYouTubeEmbedId(song));
const getSetlistCardId = (song) => String(song?.ss_id || song?.id || song?.songid || song?.songId || song?.ID || "");
const YOUTUBE_BUTTON_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 150 150" aria-hidden="true" focusable="false">
    <path
      id="Selection"
      fill="red"
      stroke="red"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mr-2"
      d="M 36.00,23.14 C 26.91,24.71 15.26,21.82 8.53,29.21 3.42,34.82 1.72,43.73 0.90,51.00 -0.72,64.03 -0.54,86.91 0.90,100.00 1.76,107.18 4.02,116.37 9.47,121.47 15.32,126.95 24.52,125.98 32.00,126.26 32.00,126.26 109.00,127.00 109.00,127.00 109.00,127.00 121.00,126.09 121.00,126.09 128.60,125.57 135.36,127.02 141.32,120.67 146.32,115.34 148.03,107.02 149.00,100.00 150.64,88.13 150.64,61.87 149.00,50.00 148.07,43.29 146.22,34.40 141.47,29.33 135.67,23.13 128.62,24.09 121.00,24.00 121.00,24.00 106.00,23.14 106.00,23.14 106.00,23.14 62.00,23.14 62.00,23.14 62.00,23.14 36.00,23.14 36.00,23.14 Z M 103.00,74.00 C 103.00,74.00 103.00,76.00 103.00,76.00 103.00,76.00 73.00,94.00 73.00,94.00 73.00,94.00 56.00,103.00 56.00,103.00 56.00,103.00 56.00,47.00 56.00,47.00 56.00,47.00 73.00,56.20 73.00,56.20 73.00,56.20 103.00,74.00 103.00,74.00 Z"
    />
  </svg>
);
const readPracticeSettings = () => {
  try {
    const raw = localStorage.getItem(PRACTICE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      tempo: toStoredNumber(parsed.tempo, PRACTICE_DEFAULTS.tempo),
      beatsPerMeasure: toStoredNumber(parsed.beatsPerMeasure, PRACTICE_DEFAULTS.beatsPerMeasure),
      suppressDownbeat: Boolean(parsed.suppressDownbeat),
      padKey: parsed.padKey || PRACTICE_DEFAULTS.padKey,
      padVolume: toStoredNumber(parsed.padVolume, PRACTICE_DEFAULTS.padVolume),
      padPan: toStoredNumber(parsed.padPan, null),
      clickVolume: toStoredNumber(parsed.clickVolume, PRACTICE_DEFAULTS.clickVolume),
      clickPan: toStoredNumber(parsed.clickPan, null),
    };
  } catch (_) {
    return null;
  }
};
const savePracticeSettings = (next) => {
  try {
    localStorage.setItem(PRACTICE_STORAGE_KEY, JSON.stringify(next));
  } catch (_) {
    // ignore storage failures
  }
};
const toStoredNumber = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};
const getPracticeSongPreset = (song) => {
  const chordPro = song?.chordpro || "";
  const tempoMatch = chordPro.match(/\{tempo:\s*([0-9]+)\s*\}/i);
  const timeMatch = chordPro.match(/\{time:\s*([0-9]+)\s*\/\s*([0-9]+)\s*\}/i);
  const bpm = tempoMatch ? Number(tempoMatch[1]) : null;
  const beatsPerMeasure = timeMatch ? Number(timeMatch[1]) : null;
  const beatUnit = timeMatch ? Number(timeMatch[2]) : null;
  const key = song?.skey?.trim?.() || "";
  const missing = [
    !key && "key",
    (!Number.isFinite(bpm) || bpm <= 0) && "tempo",
    (!Number.isFinite(beatsPerMeasure) || beatsPerMeasure <= 0 || !Number.isFinite(beatUnit) || beatUnit <= 0) && "time signature",
  ].filter(Boolean);
  return {
    bpm,
    beatsPerMeasure,
    key,
    missing,
    timeSignature: Number.isFinite(beatsPerMeasure) && Number.isFinite(beatUnit) ? `${beatsPerMeasure}/${beatUnit}` : null,
  };
};
const normalizeSelectionValue = (value) => (value == null || value === "" ? "" : String(value));
const isSameSelection = (a, b) => normalizeSelectionValue(a) === normalizeSelectionValue(b);
const getSongBandId = (song, fallback = null) => {
  const bandId = song?.band_id ?? song?.bandId ?? fallback;
  return bandId == null || bandId === "" ? null : String(bandId);
};
const getSelectedBandStorageKey = (userId) => (userId ? `${SELECTED_BAND_STORAGE_PREFIX}:${userId}` : null);
const readStoredSelectedBand = (userId) => {
  const key = getSelectedBandStorageKey(userId);
  if (!key) return "";
  try {
    return normalizeSelectionValue(localStorage.getItem(key));
  } catch (_) {
    return "";
  }
};
const writeStoredSelectedBand = (userId, bandId) => {
  const key = getSelectedBandStorageKey(userId);
  if (!key) return;
  try {
    const normalizedBandId = normalizeSelectionValue(bandId);
    if (normalizedBandId) {
      localStorage.setItem(key, normalizedBandId);
    } else {
      localStorage.removeItem(key);
    }
  } catch (_) {
    // ignore storage failures
  }
};
const resolveBandSelection = (bandList = [], preferredBandId = "") => {
  if (!bandList.length) return "";
  const preferred = normalizeSelectionValue(preferredBandId);
  const matchingBand = bandList.find((band) => isSameSelection(band?.id, preferred));
  return normalizeSelectionValue(matchingBand?.id ?? bandList[0]?.id);
};
const resolveServiceSelection = (serviceList = [], preferredServiceId = "", bandId = "") => {
  if (!serviceList.length) return "";
  const normalizedBandId = normalizeSelectionValue(bandId);
  const preferred = normalizeSelectionValue(preferredServiceId);
  const matchingService = serviceList.find((service) => {
    if (!isSameSelection(service?.id, preferred)) return false;
    if (!normalizedBandId) return true;
    return isSameSelection(service?.band_id, normalizedBandId);
  });
  if (matchingService) {
    return normalizeSelectionValue(matchingService.id);
  }
  const today = new Date().toISOString().split("T")[0];
  const upcoming = serviceList.find((service) => service?.sdate >= today);
  return normalizeSelectionValue(upcoming?.id ?? serviceList[0]?.id);
};
const readMixSettings = () => {
  try {
    const raw = localStorage.getItem(MIX_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      clickVolume: toStoredNumber(parsed.clickVolume, 1),
      clickPan: toStoredNumber(parsed.clickPan, -1),
      guideVolume: toStoredNumber(parsed.guideVolume, 1),
      guidePan: toStoredNumber(parsed.guidePan, -1),
      padVolume: toStoredNumber(parsed.padVolume, 1),
      padPan: toStoredNumber(parsed.padPan, 1),
    };
  } catch (_) {
    return null;
  }
};
const readStoredClickPreset = () => {
  try {
    const preset = localStorage.getItem(CLICK_PRESET_STORAGE_KEY);
    return CLICK_PRESETS[preset] ? preset : DEFAULT_CLICK_PRESET;
  } catch (_) {
    return DEFAULT_CLICK_PRESET;
  }
};
const writeStoredClickPreset = (preset) => {
  if (!CLICK_PRESETS[preset]) return;
  try {
    localStorage.setItem(CLICK_PRESET_STORAGE_KEY, preset);
  } catch (_) {
    // ignore storage failures
  }
};
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Toast, ToastContainer } from "react-bootstrap";
import useStemPlayer from "./hooks/useStemPlayer";
import usePadPlayer from "./hooks/usePadPlayer";
import useClickTrack, { CLICK_PRESETS, DEFAULT_CLICK_PRESET } from "./hooks/useClickTrack";
import useBeatClock from "./hooks/useBeatClock";
import SectionEditorModal from "./components/SectionEditorModal";
import useGuideTimeline from "./hooks/useGuideTimeline";
import useGuidePlayer from "./hooks/useGuidePlayer";
import getSharedAudioContext from "./hooks/audioContext";
import useLeaderPresence from "./hooks/useLeaderPresence";
import useLeaderTransportBroadcast from "./hooks/useLeaderTransportBroadcast";
import useFollowerTransportSync from "./hooks/useFollowerTransportSync";
import { buildMeasureBoundaryBeats, deriveTimingMeta, getNextBoundaryBeat } from "./utils/timing";
import { acquireBandLeaderChannel, releaseBandLeaderChannel } from "./realtime/bandLeaderChannel";
import { getCapabilities } from "./capabilities";
import {
  cacheBands,
  getCachedBands,
  cacheServices,
  getCachedServices,
  cacheSongs,
  getCachedSongs,
  cacheSetlist,
  getCachedSetlist,
  cacheSongSections,
  getCachedSongSections,
  cacheArrangement,
  getCachedArrangement,
  getCachedArrangementSections,
  clearCachedSongSections,
  clearCachedArrangementsForSong,
  saveCanvasNotes,
  getCanvasNotes,
  clearCanvasNotes,
  savePreference,
  getPreference,
} from "./dbSafe";

function App() {
  const navigate = useNavigate();
  const transportSyncTimerRef = useRef(null);
  const lastLeaderSongIdRef = useRef(null);
  const capabilities = useMemo(() => getCapabilities(), []);
  const limitedMode = capabilities.forceLite;
  const limitedReason = capabilities.limitedReason;
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill = params.get("prefillEmail");
    if (prefill) {
      setEmail(prefill);
    }
  }, []);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [songs, setSongs] = useState([]);
  const [bands, setBands] = useState([]);
  const [selectedBand, setSelectedBand] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [chordKey, setChordKey] = useState("");
  const [fontSize, setFontSize] = useState(() => {
    const savedFontSize = localStorage.getItem("chordProFontSize");
    return savedFontSize ? parseFloat(savedFontSize) : 16;
  });
  const [showVideo, setShowVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [setlistPlayerSongId, setSetlistPlayerSongId] = useState(null);
  const [setlistPlayerEmbedId, setSetlistPlayerEmbedId] = useState(null);
  const [setlistPlayerOpen, setSetlistPlayerOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const selectedBandRef = useRef("");
  const selectedServiceRef = useRef("");
  const fetchBandsRequestIdRef = useRef(0);
  const fetchServicesRequestIdRef = useRef(0);
  const [sections, setSections] = useState([]);
  const [showArrangementEditor, setShowArrangementEditor] = useState(false);
  const [currentArrangement, setCurrentArrangement] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [arrangementSections, setArrangementSections] = useState([]);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [sectionEditorSongId, setSectionEditorSongId] = useState(null);
  const [sectionEditorBandId, setSectionEditorBandId] = useState(null);
  const [useMonolithicView, setUseMonolithicView] = useState(false);
  const [pendingSetlistSongIds, setPendingSetlistSongIds] = useState({});
  const [isLeader, setIsLeader] = useState(null); // null = loading
  const [leaderCheckDone, setLeaderCheckDone] = useState(false);
  const [tracksEnabled, setTracksEnabled] = useState(true);
  const [padKeyOverride, setPadKeyOverride] = useState(null);
  const stemPlayer = useStemPlayer({
    songId: selectedSong?.id || selectedSong?.songid,
    isLeader,
    disabled: limitedMode || !capabilities.supportsStems,
  });
  const padPrefKey = useMemo(() => {
    const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
    return songId ? `${PAD_OVERRIDE_PREFIX}:${songId}` : null;
  }, [selectedSong?.ID, selectedSong?.id, selectedSong?.songId, selectedSong?.songid]);
  const padDisplayKey = padKeyOverride || chordKey || selectedSong?.skey || "C";
  const padLoadKey = normalizePadKey(padDisplayKey);
  const padPlayer = usePadPlayer({ songKey: padLoadKey, displayKey: padDisplayKey });
  // Guide timeline is the timing source for click/pad alignment and scrub math.
  const guideTimeline = useGuideTimeline({ arrangementId: currentArrangement?.id });
  const [playbackMode, setPlaybackMode] = useState(null);
  const [guideState, setGuideState] = useState(() => {
    const stored = readMixSettings();
    return {
      isPlaying: false,
      volume: stored?.guideVolume ?? 1,
      pan: stored?.guidePan ?? -1,
    };
  });
  const [transportStartAt, setTransportStartAt] = useState(null);
  const [guideStartBeat, setGuideStartBeat] = useState(0);
  const [guideBeatOffset, setGuideBeatOffset] = useState(0);
  const [clickBeatOffset, setClickBeatOffset] = useState(0);
  const [guideLeadInEnabled, setGuideLeadInEnabled] = useState(true);
  const [notesVisibleOn, setNotesVisibleOn] = useState(() => {
    try {
      const stored = localStorage.getItem("ct-notes-enabled");
      if (stored === null) return true;
      return stored === "1";
    } catch (_) {
      return true;
    }
  });
  const [chordDrawerOpen, setChordDrawerOpen] = useState(false);
  const [clickPreset, setClickPreset] = useState(() => readStoredClickPreset());
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [practiceNavOpen, setPracticeNavOpen] = useState(false);
  const [practiceSettings, setPracticeSettings] = useState(() => readPracticeSettings() || null);
  const [practiceTempo, setPracticeTempo] = useState(PRACTICE_DEFAULTS.tempo);
  const [practiceBeatsPerMeasure, setPracticeBeatsPerMeasure] = useState(PRACTICE_DEFAULTS.beatsPerMeasure);
  const [practiceSuppressDownbeat, setPracticeSuppressDownbeat] = useState(false);
  const [practicePadKey, setPracticePadKey] = useState(PRACTICE_DEFAULTS.padKey);
  const [practicePadVolume, setPracticePadVolume] = useState(PRACTICE_DEFAULTS.padVolume);
  const [practicePadPan, setPracticePadPan] = useState(1);
  const [practiceClickVolume, setPracticeClickVolume] = useState(PRACTICE_DEFAULTS.clickVolume);
  const [practiceClickPan, setPracticeClickPan] = useState(-1);
  const [pendingPracticeClickRestart, setPendingPracticeClickRestart] = useState(null);
  const [selectedPracticeSongId, setSelectedPracticeSongId] = useState(null);
  const practicePadLoadKey = normalizePadKey(practicePadKey);
  const practicePadPlayer = usePadPlayer({ songKey: practicePadLoadKey, displayKey: practicePadKey, fadeDuration: 4 });
  const [isEditing, setIsEditing] = useState(false); // edit mode: drawing enabled
  const [isManualRefreshRunning, setIsManualRefreshRunning] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [sectionStartAt, setSectionStartAt] = useState(null);
  const [sectionStartBeat, setSectionStartBeat] = useState(0);
  const [accentGate, setAccentGate] = useState(null);
  const timingMeta = useMemo(() => deriveTimingMeta({ chordPro: selectedSong?.chordpro, guideTimeline: guideTimeline?.timeline }), [guideTimeline?.timeline, selectedSong?.chordpro]);
  const beatClock = useBeatClock({ bpm: timingMeta?.bpm, beatsPerMeasure: timingMeta?.beatsPerMeasure });
  const measureBoundaries = useMemo(() => {
    const suppressMap = new Map(
      (sections || []).map((section) => {
        const key = section?.id ?? section?.section_id ?? section?.arrangement_section_id;
        return [String(key), Boolean(section?.suppress_downbeat)];
      }),
    );
    const timelineSections = (guideTimeline?.timeline?.sections || []).map((section) => {
      const key = section?.section_id ?? section?.id;
      const suppress = suppressMap.get(String(key)) || false;
      return { ...section, suppress_downbeat: suppress };
    });
    return buildMeasureBoundaryBeats(timelineSections, timingMeta?.beatsPerMeasure);
  }, [guideTimeline?.timeline?.sections, sections, timingMeta?.beatsPerMeasure]);
  const mixInitRef = useRef(false);
  const clickSound = CLICK_PRESETS[clickPreset] || CLICK_PRESETS[DEFAULT_CLICK_PRESET];
  const clickTrack = useClickTrack({
    beatClock,
    measureBoundaries,
    accentGate,
    beatOffset: clickBeatOffset,
    clickSound,
  });
  const practiceBeatClock = useBeatClock({ bpm: practiceTempo, beatsPerMeasure: practiceBeatsPerMeasure });
  const practiceClickTrack = useClickTrack({
    beatClock: practiceBeatClock,
    suppressAccent: practiceSuppressDownbeat,
  });
  const isMember = isLeader === false;
  const scrollContainerRef = useRef(null);
  const sectionRefs = useRef({});
  const activeSectionRef = useRef(null);
  const sectionScrollTimeoutRef = useRef(null);
  const sectionIntervalRef = useRef(null);
  const scrollLogThrottleRef = useRef(0);
  const sectionScrollBlockedUntilRef = useRef(0);
  const manualSectionLockRef = useRef({ active: false, lockedId: null });
  const lastPlaybackSectionIdRef = useRef(null);
  const openChordRequestIdRef = useRef(0);
  const beatDebugEnabled = useCallback(() => {
    try {
      return window?.__ctDebugBeat === true || localStorage.getItem("ct-debug-beat") === "1";
    } catch (err) {
      return false;
    }
  }, []);
  useGuidePlayer({
    timeline: guideTimeline?.timeline,
    beatClock,
    isEnabled: guideState.isPlaying,
    startBeat: guideStartBeat,
    beatOffset: guideBeatOffset,
    includeLeadIn: guideLeadInEnabled,
    volume: guideState.volume,
    pan: guideState.pan,
  });
  useEffect(() => {
    if (mixInitRef.current) return;
    mixInitRef.current = true;
    const stored = readMixSettings();
    if (!stored) return;
    if (Number.isFinite(stored.clickVolume)) {
      clickTrack.setClickVolume(stored.clickVolume);
    }
    if (Number.isFinite(stored.clickPan)) {
      clickTrack.setClickPan(stored.clickPan);
    }
    if (Number.isFinite(stored.padVolume)) {
      padPlayer.setPadVolume(stored.padVolume);
    }
    if (Number.isFinite(stored.padPan)) {
      padPlayer.setPadPan(stored.padPan);
    }
  }, [clickTrack, padPlayer]);
  useEffect(() => {
    if (!isLeader) return;
    const payload = {
      clickVolume: toStoredNumber(clickTrack.volume, 1),
      clickPan: toStoredNumber(clickTrack.pan, -1),
      guideVolume: toStoredNumber(guideState.volume, 1),
      guidePan: toStoredNumber(guideState.pan, -1),
      padVolume: toStoredNumber(padPlayer.volume, 1),
      padPan: toStoredNumber(padPlayer.pan, 1),
    };
    try {
      localStorage.setItem(MIX_STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignore storage failures
    }
  }, [clickTrack.volume, clickTrack.pan, guideState.volume, guideState.pan, isLeader, padPlayer.volume, padPlayer.pan]);
  useEffect(() => {
    let mounted = true;
    getPreference("tracksEnabled", true)
      .then((value) => {
        if (!mounted) return;
        setTracksEnabled(Boolean(value));
      })
      .catch(() => {
        // ignore preference failures
      });
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    setSetlistPlayerSongId(null);
    setSetlistPlayerEmbedId(null);
    setSetlistPlayerOpen(false);
  }, [selectedService]);
  useEffect(() => {
    let mounted = true;
    if (!padPrefKey || !chordKey) {
      setPadKeyOverride(null);
      return () => {
        mounted = false;
      };
    }
    setPadKeyOverride(null);
    getPreference(padPrefKey, null)
      .then((value) => {
        if (!mounted) return;
        if (value?.key && value?.chordKey === chordKey) {
          setPadKeyOverride(value.key);
        } else {
          setPadKeyOverride(null);
        }
      })
      .catch(() => {
        if (mounted) {
          setPadKeyOverride(null);
        }
      });
    return () => {
      mounted = false;
    };
  }, [chordKey, padPrefKey]);
  useEffect(() => {
    clickTrack.stopClick();
    setClickBeatOffset(0);
  }, [selectedSong?.id, clickTrack.stopClick, setClickBeatOffset]);
  const lastChordKeyRef = useRef(null);
  useEffect(() => {
    if (!chordKey) return;
    if (lastChordKeyRef.current === null) {
      lastChordKeyRef.current = chordKey;
      return;
    }
    if (lastChordKeyRef.current === chordKey) return;
    lastChordKeyRef.current = chordKey;
    setPadKeyOverride(null);
    if (padPrefKey) {
      savePreference(padPrefKey, { key: null, chordKey });
    }
  }, [chordKey, padPrefKey]);
  const guideData = guideTimeline?.timeline;
  const guideMissingMeasures = guideData?.ok === false && guideData?.error === "missing_measures";
  const hasGuideStems = guideData?.has_stems ?? guideData?.hasStems;
  const hasPlayableStems = !limitedMode && Boolean(stemPlayer.stemsPackageId);
  const effectivePlaybackMode = limitedMode ? "lite" : playbackMode || (hasPlayableStems ? "stems" : "lite");
  const isTimelinePlaybackActive = (effectivePlaybackMode === "lite" || effectivePlaybackMode === "stems") && guideState.isPlaying;
  const selectedSongYouTubeId = getYouTubeEmbedId(selectedSong);
  const leaderName = username || user?.email || "Leader";
  const {
    activeLeaders,
    selectedLeaderId,
    setSelectedLeaderId,
    isOnline: isPresenceOnline,
    requestLeaderPresence,
  } = useLeaderPresence({
    bandId: selectedBand,
    isLeader,
    leaderUserId: user?.id,
    leaderName,
  });
  const visibleFollowLeader = useMemo(() => {
    if (!selectedLeaderId) return null;
    return activeLeaders.find((leader) => String(leader.leader_user_id) === String(selectedLeaderId)) || null;
  }, [activeLeaders, selectedLeaderId]);
  const { sendSongChange, sendPlay, sendJump, sendStop } = useLeaderTransportBroadcast({ bandId: selectedBand });
  const wasGuidePlayingRef = useRef(false);
  const wasClickPlayingRef = useRef(false);
  const lastSongBroadcastRef = useRef({ songId: null });
  const wasModalOpenRef = useRef(false);
  const canShowPlaybackIsland = tracksEnabled && (isLeader || isMember);
  const stopFollowerPlayback = useCallback(
    (options = {}) => {
      const { keepActiveSection = false } = options;
      clickTrack.stopClick?.();
      stemPlayer.stop?.();
      padPlayer.stopPad?.();
      setTransportStartAt(null);
      setSectionStartAt(null);
      setSectionStartBeat(0);
      setGuideStartBeat(0);
      setGuideBeatOffset(0);
      setAccentGate(null);
      setClickBeatOffset(0);
      if (!keepActiveSection) {
        setActiveSectionId(null);
      }
      if (guideState.isPlaying) {
        setGuideState((prev) => ({ ...prev, isPlaying: false }));
        setGuideLeadInEnabled(true);
      }
    },
    [clickTrack.stopClick, guideState.isPlaying, padPlayer.stopPad, setAccentGate, setClickBeatOffset, stemPlayer.stop],
  );
  const ensureMemberLocalPlaybackStart = useCallback(() => {
    if (!isMember) return;
    if (selectedLeaderId) {
      setSelectedLeaderId(null);
    }
    if (clickTrack.pan !== 0) {
      clickTrack.setClickPan(0);
    }
    if (guideState.pan !== 0) {
      setGuideState((prev) => (prev.pan === 0 ? prev : { ...prev, pan: 0 }));
    }
    if (padPlayer.pan !== 0) {
      padPlayer.setPadPan(0);
    }
    if (stemPlayer.tracks?.length) {
      stemPlayer.tracks.forEach((track) => {
        if ((track.pan_default ?? 0) !== 0) {
          stemPlayer.setTrackPan(track.id, 0, { persist: false });
        }
      });
    }
  }, [clickTrack, guideState.pan, isMember, padPlayer, selectedLeaderId, setSelectedLeaderId, stemPlayer]);

  useEffect(() => {
    if (!showModal) {
      lastSongBroadcastRef.current = { songId: null };
    }
  }, [showModal, selectedBand]);
  useEffect(() => {
    if (showModal && !wasModalOpenRef.current) {
      if (selectedLeaderId) {
        setSelectedLeaderId(null);
      }
    }
    wasModalOpenRef.current = showModal;
  }, [selectedLeaderId, setSelectedLeaderId, showModal]);
  useEffect(() => {
    try {
      localStorage.setItem("ct-notes-enabled", notesVisibleOn ? "1" : "0");
    } catch (_) {
      // ignore storage failures
    }
  }, [notesVisibleOn]);
  useEffect(() => {
    if (!leaderCheckDone) return;
    const stored = practiceSettings;
    const defaultPadPan = isLeader ? 1 : 0;
    const defaultClickPan = isLeader ? -1 : 0;
    const next = {
      tempo: stored?.tempo ?? PRACTICE_DEFAULTS.tempo,
      beatsPerMeasure: stored?.beatsPerMeasure ?? PRACTICE_DEFAULTS.beatsPerMeasure,
      suppressDownbeat: stored?.suppressDownbeat ?? false,
      padKey: stored?.padKey ?? PRACTICE_DEFAULTS.padKey,
      padVolume: stored?.padVolume ?? PRACTICE_DEFAULTS.padVolume,
      padPan: Number.isFinite(stored?.padPan) ? stored.padPan : defaultPadPan,
      clickVolume: stored?.clickVolume ?? PRACTICE_DEFAULTS.clickVolume,
      clickPan: Number.isFinite(stored?.clickPan) ? stored.clickPan : defaultClickPan,
    };
    setPracticeTempo(next.tempo);
    setPracticeBeatsPerMeasure(next.beatsPerMeasure);
    setPracticeSuppressDownbeat(next.suppressDownbeat);
    setPracticePadKey(next.padKey);
    setPracticePadVolume(next.padVolume);
    setPracticePadPan(next.padPan);
    setPracticeClickVolume(next.clickVolume);
    setPracticeClickPan(next.clickPan);
  }, [isLeader, leaderCheckDone, practiceSettings]);
  useEffect(() => {
    if (!notesVisibleOn && isEditing) {
      setIsEditing(false);
    }
  }, [isEditing, notesVisibleOn]);
  useEffect(() => {
    practicePadPlayer.setPadVolume(practicePadVolume);
  }, [practicePadVolume, practicePadPlayer]);
  useEffect(() => {
    practicePadPlayer.setPadPan(practicePadPan);
  }, [practicePadPan, practicePadPlayer]);
  useEffect(() => {
    practiceClickTrack.setClickVolume(practiceClickVolume);
  }, [practiceClickTrack, practiceClickVolume]);
  useEffect(() => {
    practiceClickTrack.setClickPan(practiceClickPan);
  }, [practiceClickTrack, practiceClickPan]);
  useEffect(() => {
    if (!pendingPracticeClickRestart || !showPracticeModal) return;
    if (practiceTempo !== pendingPracticeClickRestart.bpm || practiceBeatsPerMeasure !== pendingPracticeClickRestart.beatsPerMeasure) return;
    const startAt = getSharedAudioContext().currentTime + 0.1;
    practiceBeatClock?.setAnchor?.(startAt, 0);
    practiceClickTrack.startClick(startAt, 0, 0);
    setPendingPracticeClickRestart(null);
  }, [pendingPracticeClickRestart, practiceBeatClock, practiceBeatsPerMeasure, practiceClickTrack, practiceTempo, showPracticeModal]);
  useEffect(() => {
    if (!practiceClickTrack.isClickPlaying) return;
    const ctx = getSharedAudioContext();
    const currentBeat = practiceBeatClock.timeToBeat?.(ctx.currentTime);
    practiceBeatClock.resetAnchor?.();
    const anchorBeat = Number.isFinite(currentBeat) ? currentBeat : 0;
    practiceBeatClock.setAnchor?.(ctx.currentTime, anchorBeat);
  }, [practiceBeatClock, practiceBeatsPerMeasure, practiceClickTrack.isClickPlaying, practiceTempo]);
  useEffect(() => {
    if (!isLeader || !showModal) return;
    const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
    if (!songId || !user?.id) return;
    const last = lastSongBroadcastRef.current;
    if (String(last.songId || "") === String(songId)) return;
    sendSongChange({
      leader_user_id: user?.id,
      song_id: songId,
      arrangement_id: currentArrangement?.id || null,
      song_title: selectedSong?.title || "Untitled",
    });
    lastSongBroadcastRef.current = { songId };
  }, [currentArrangement?.id, isLeader, selectedSong?.id, selectedSong?.songid, selectedSong?.songId, selectedSong?.ID, sendSongChange, showModal, user?.id]);
  useEffect(() => {
    lastLeaderSongIdRef.current = null;
  }, [selectedLeaderId]);
  useEffect(() => {
    if (!guideState.isPlaying) {
      if (isLeader && wasGuidePlayingRef.current && user?.id) {
        sendStop({ leader_user_id: user.id });
      }
      wasGuidePlayingRef.current = false;
      return;
    }
    if (!isLeader || wasGuidePlayingRef.current) return;
    const bpm = Number(guideData?.bpm || guideData?.tempo);
    const beatsPerMeasureRaw = Number(guideData?.beats_per_measure || guideData?.beatsPerMeasure);
    const beatsPerMeasure = Number.isFinite(beatsPerMeasureRaw) && beatsPerMeasureRaw > 0 ? beatsPerMeasureRaw : 4;
    if (!Number.isFinite(bpm) || bpm <= 0 || !Number.isFinite(sectionStartAt) || !user?.id) return;
    const ctx = getSharedAudioContext();
    const msPerBeat = 60000 / bpm;
    const startAtUtc = Date.now() + (sectionStartAt - ctx.currentTime) * 1000;
    const anchorUtc = startAtUtc - (Number(sectionStartBeat) || 0) * msPerBeat;
    sendPlay({
      leader_user_id: user?.id,
      bpm,
      beats_per_measure: beatsPerMeasure,
      timeline_anchor_utc: anchorUtc,
    });
    wasGuidePlayingRef.current = true;
  }, [guideData?.beatsPerMeasure, guideData?.beats_per_measure, guideData?.bpm, guideData?.tempo, guideState.isPlaying, isLeader, sectionStartAt, sectionStartBeat, sendPlay, sendStop, user?.id]);
  useEffect(() => {
    if (!clickTrack.isClickPlaying) {
      if (isLeader && wasClickPlayingRef.current && user?.id) {
        sendStop({ leader_user_id: user.id });
      }
      wasClickPlayingRef.current = false;
      return;
    }
    if (!isLeader || wasClickPlayingRef.current) return;
    wasClickPlayingRef.current = true;
  }, [clickTrack.isClickPlaying, isLeader, sendStop, user?.id]);

  const startLiteTransport = useCallback(() => {
    const ctx = getSharedAudioContext();
    try {
      ctx.resume?.();
    } catch (_) {
      // ignore resume errors
    }
    return ctx.currentTime + 0.1;
  }, []);

  const registerSectionRef = useCallback((sectionId, node) => {
    if (!sectionId) return;
    const key = String(sectionId);
    if (node) {
      sectionRefs.current[key] = node;
    } else {
      delete sectionRefs.current[key];
    }
  }, []);

  const getScrollContainer = useCallback(() => {
    return scrollContainerRef.current || document.querySelector(".modal-body");
  }, []);

  const handleToggleClick = useCallback(() => {
    if (beatDebugEnabled()) {
      console.log("[click-debug] toggle", {
        isClickPlaying: clickTrack.isClickPlaying,
        isGuidePlaying: guideState.isPlaying,
      });
    }
    if (clickTrack.isClickPlaying) {
      clickTrack.stopClick();
      setTransportStartAt(null);
      setGuideStartBeat(0);
      setGuideBeatOffset(0);
      setSectionStartAt(null);
      setSectionStartBeat(0);
      setActiveSectionId(null);
      setAccentGate(null);
      setClickBeatOffset(0);
      if (guideState.isPlaying) {
        setGuideState((prev) => ({ ...prev, isPlaying: false }));
        setGuideLeadInEnabled(true);
      }
      return;
    }
    ensureMemberLocalPlaybackStart();
    const startAt = startLiteTransport();
    let initialOffset = 0;
    if (activeSectionId && guideData?.sections?.length) {
      const section = guideData.sections.find((item) => String(item.section_id ?? item.id ?? "") === String(activeSectionId));
      const startBeat = Number(section?.start_beat ?? section?.startBeat ?? 0);
      if (Number.isFinite(startBeat)) {
        initialOffset = startBeat;
      }
    }
    setClickBeatOffset(initialOffset);
    beatClock?.setAnchor?.(startAt, 0);
    clickTrack.startClick(startAt, 0, initialOffset);
    setTransportStartAt(startAt);
    setSectionStartAt(startAt);
    setSectionStartBeat(0);
    setAccentGate(null);
  }, [activeSectionId, beatClock, clickTrack, ensureMemberLocalPlaybackStart, guideData?.sections, guideState.isPlaying, setAccentGate, startLiteTransport]);

  const handleToggleGuide = useCallback(async () => {
    if (guideMissingMeasures) return;
    if (guideState.isPlaying) {
      setGuideState((prev) => ({ ...prev, isPlaying: false }));
      setGuideStartBeat(0);
      setGuideBeatOffset(0);
      setGuideLeadInEnabled(true);
      setSectionStartAt(null);
      setSectionStartBeat(0);
      setActiveSectionId(null);
      setAccentGate(null);
      setClickBeatOffset(0);
      if (effectivePlaybackMode === "stems") {
        stemPlayer.stop?.();
      }
      return;
    }
    ensureMemberLocalPlaybackStart();
    const ctx = getSharedAudioContext();
    const secondsPerBeat = beatClock?.secondsPerBeat;
    const beatsPerMeasure = beatClock?.beatsPerMeasure;
    if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) return;
    const shouldStartStems = effectivePlaybackMode === "stems" && hasPlayableStems;
    if (shouldStartStems) {
      await stemPlayer.preload?.();
    }
    let guideOffset = 0;
    let clickOffset = 0;
    let alignedStartAt = null;
    if (clickTrack.isClickPlaying) {
      const currentBeatIndex = beatClock?.timeToBeat?.(ctx.currentTime);
      const safeCurrentBeat = Number.isFinite(currentBeatIndex) ? currentBeatIndex : 0;
      const nextClickBoundaryIndex = Math.ceil((safeCurrentBeat - 0.0001) / beatsPerMeasure) * beatsPerMeasure;
      const cueBeat = 0 - beatsPerMeasure;
      clickOffset = cueBeat - nextClickBoundaryIndex;
      guideOffset = nextClickBoundaryIndex - cueBeat;
      setClickBeatOffset(clickOffset);
      setGuideBeatOffset(guideOffset);
      alignedStartAt = beatClock?.beatToTime?.(0 + guideOffset);
      setSectionStartAt(alignedStartAt);
      setGuideLeadInEnabled(false);
    } else {
      const clickStartAt = ctx.currentTime + 0.1;
      const includeLeadIn = true;
      const anchorStartBeat = 0 - beatsPerMeasure * (includeLeadIn ? 2 : 1);
      setClickBeatOffset(0);
      setGuideBeatOffset(0);
      beatClock?.setAnchor?.(clickStartAt, anchorStartBeat);
      clickTrack.startClick(clickStartAt, anchorStartBeat, 0);
      setTransportStartAt(clickStartAt);
      alignedStartAt = beatClock?.beatToTime?.(0);
      setSectionStartAt(alignedStartAt);
      setGuideLeadInEnabled(includeLeadIn);
    }
    setSectionStartBeat(0);
    setGuideStartBeat(0);
    setGuideState((prev) => ({ ...prev, isPlaying: true }));
    setAccentGate(null);
    if (shouldStartStems && Number.isFinite(alignedStartAt)) {
      if (stemPlayer.isPlaying) {
        stemPlayer.stop(0);
      }
      stemPlayer.play({ startAt: alignedStartAt, offset: 0 });
    }
  }, [
    beatClock?.beatsPerMeasure,
    beatClock?.secondsPerBeat,
    clickBeatOffset,
    clickTrack,
    clickTrack.isClickPlaying,
    effectivePlaybackMode,
    ensureMemberLocalPlaybackStart,
    guideMissingMeasures,
    guideState.isPlaying,
    hasPlayableStems,
    measureBoundaries?.boundaryArray,
    setAccentGate,
    setClickBeatOffset,
    setGuideBeatOffset,
    stemPlayer.isPlaying,
    stemPlayer.play,
    stemPlayer.preload,
    stemPlayer.stop,
  ]);

  const handleTogglePad = useCallback(async () => {
    if (!padPlayer.isPlaying) {
      ensureMemberLocalPlaybackStart();
    }
    await padPlayer.togglePad();
  }, [ensureMemberLocalPlaybackStart, padPlayer]);

  const handlePracticeTogglePad = useCallback(async () => {
    await practicePadPlayer.togglePad();
  }, [practicePadPlayer]);

  const handlePracticeToggleClick = useCallback(() => {
    if (practiceClickTrack.isClickPlaying) {
      practiceClickTrack.stopClick();
      return;
    }
    const startAt = getSharedAudioContext().currentTime + 0.1;
    practiceBeatClock?.setAnchor?.(startAt, 0);
    practiceClickTrack.startClick(startAt, 0, 0);
  }, [practiceBeatClock, practiceClickTrack]);

  const handlePracticeSongPreset = useCallback(
    (song) => {
      const preset = getPracticeSongPreset(song);
      if (preset.missing.length) {
        setError(`${song?.title || "This song"} is missing ${preset.missing.join(", ")}. Add the missing song information before using this preset.`);
        return;
      }
      const shouldRestartClick = practiceClickTrack.isClickPlaying;
      if (shouldRestartClick) {
        practiceClickTrack.stopClick();
      }
      setPracticePadKey(preset.key);
      setPracticeTempo(preset.bpm);
      setPracticeBeatsPerMeasure(preset.beatsPerMeasure);
      setSelectedPracticeSongId(song?.ss_id || song?.songid || song?.id || null);
      if (shouldRestartClick) {
        setPendingPracticeClickRestart({ bpm: preset.bpm, beatsPerMeasure: preset.beatsPerMeasure });
      }
    },
    [practiceClickTrack.isClickPlaying, practiceClickTrack.stopClick],
  );

  const requestSectionJump = useCallback(
    (sectionId) => {
      const isPlayableMode = effectivePlaybackMode === "lite" || effectivePlaybackMode === "stems";
      if (!isPlayableMode || !isLeader || !guideTimeline?.timeline?.sections?.length) return;
      const timelineSections = guideTimeline.timeline.sections || [];
      const target = timelineSections.find((section) => String(section.section_id) === String(sectionId));
      if (!target) return;
      const targetIndex = timelineSections.findIndex((section) => String(section.section_id) === String(sectionId));
      if (guideState.isPlaying && targetIndex >= 0 && user?.id) {
        sendJump({
          leader_user_id: user?.id,
          targetSectionIndex: targetIndex,
        });
      }
      const ctx = getSharedAudioContext();
      const secondsPerBeat = beatClock?.secondsPerBeat;
      const beatsPerMeasure = beatClock?.beatsPerMeasure;
      if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) return;
      const targetStartBeat = Number(target.start_beat || 0);
      const cueBeat = targetStartBeat - beatsPerMeasure;
      if (!beatClock?.isAnchored?.()) {
        const clickStartAt = ctx.currentTime + 0.1;
        setClickBeatOffset(0);
        setGuideBeatOffset(0);
        beatClock?.setAnchor?.(clickStartAt, cueBeat);
        clickTrack.startClick(clickStartAt, cueBeat, 0);
        setTransportStartAt(clickStartAt);
      }

      const currentBeatIndex = beatClock?.timeToBeat?.(ctx.currentTime);
      const safeCurrentBeat = Number.isFinite(currentBeatIndex) ? currentBeatIndex : 0;
      const nextClickBoundaryIndex = Math.ceil((safeCurrentBeat - 0.0001) / beatsPerMeasure) * beatsPerMeasure;
      const clickOffset = cueBeat - nextClickBoundaryIndex;
      const guideOffset = nextClickBoundaryIndex - cueBeat;
      setClickBeatOffset(clickOffset);
      setGuideBeatOffset(guideOffset);

      setGuideLeadInEnabled(false);
      setAccentGate({ muteUntilBeat: targetStartBeat });
      setSectionStartBeat(targetStartBeat);
      const alignedStartAt = beatClock?.beatToTime?.(targetStartBeat + guideOffset);
      setSectionStartAt(Number.isFinite(alignedStartAt) ? alignedStartAt : null);
      setGuideStartBeat(targetStartBeat);
      setGuideState((prev) => ({ ...prev, isPlaying: true }));
      if (effectivePlaybackMode === "stems" && hasPlayableStems && Number.isFinite(secondsPerBeat)) {
        const targetOffset = Math.max(0, targetStartBeat * secondsPerBeat);
        if (stemPlayer.isPlaying) {
          stemPlayer.stop(targetOffset);
        }
        if (Number.isFinite(alignedStartAt)) {
          stemPlayer.play({ startAt: alignedStartAt, offset: targetOffset });
        }
      }

      if (beatDebugEnabled()) {
        console.log("[jump-debug] schedule", {
          currentAudioTime: ctx.currentTime,
          currentBeat: safeCurrentBeat,
          nextClickBoundaryIndex,
          cueBeat,
          clickOffset,
          guideOffset,
          targetStartBeat,
          alignedStartAt,
        });
      }
    },
    [
      beatClock,
      beatDebugEnabled,
      clickBeatOffset,
      clickTrack,
      effectivePlaybackMode,
      guideState.isPlaying,
      guideTimeline?.timeline?.sections,
      hasPlayableStems,
      isLeader,
      measureBoundaries?.boundaryArray,
      sendJump,
      setAccentGate,
      setClickBeatOffset,
      setGuideBeatOffset,
      stemPlayer.isPlaying,
      stemPlayer.play,
      stemPlayer.stop,
      user?.id,
    ],
  );

  useEffect(() => {
    activeSectionRef.current = activeSectionId ? String(activeSectionId) : null;
  }, [activeSectionId]);

  useEffect(() => {
    const sectionsForSync = guideData?.sections || [];
    const bpm = Number(guideData?.bpm || guideData?.tempo);
    const beatsPerMeasureRaw = Number(guideData?.beats_per_measure || guideData?.beatsPerMeasure);
    const beatsPerMeasure = Number.isFinite(beatsPerMeasureRaw) && beatsPerMeasureRaw > 0 ? beatsPerMeasureRaw : 4;
    if (sectionIntervalRef.current) {
      clearInterval(sectionIntervalRef.current);
      sectionIntervalRef.current = null;
    }
    if (!isTimelinePlaybackActive) {
      console.log("[section-sync] skip: guide not playing");
      return;
    }
    if (!sectionsForSync.length) {
      console.log("[section-sync] skip: no timeline sections");
      return;
    }
    if (guideData?.ok === false) {
      console.log("[section-sync] skip: timeline not ok");
      return;
    }
    if (!Number.isFinite(bpm) || bpm <= 0) {
      console.log("[section-sync] skip: invalid bpm", bpm);
      return;
    }
    if (typeof sectionStartAt !== "number") {
      console.log("[section-sync] skip: no transport start");
      return;
    }

    const ctx = getSharedAudioContext();
    const secondsPerBeat = 60 / bpm;
    const minStartBeat = Number.isFinite(Number(sectionStartBeat)) ? Number(sectionStartBeat) : 0;
    const candidateSections = sectionsForSync.filter((section) => Number(section.start_beat ?? section.startBeat ?? 0) >= minStartBeat);
    const orderedSections = candidateSections.slice().sort((a, b) => Number(a.start_beat ?? a.startBeat ?? 0) - Number(b.start_beat ?? b.startBeat ?? 0));

    const tick = () => {
      const elapsedSeconds = ctx.currentTime - sectionStartAt;
      if (elapsedSeconds < 0) return;
      const beatsElapsed = (sectionStartBeat || 0) + elapsedSeconds / secondsPerBeat;
      const compareBeat = beatsElapsed;
      const currentSection = orderedSections.find((section) => {
        const startBeat = Number(section.start_beat ?? section.startBeat ?? 0);
        const endBeat = Number(section.end_beat ?? section.endBeat ?? Infinity);
        return compareBeat >= startBeat && compareBeat < endBeat;
      });
      const nextActiveId = currentSection?.id ?? currentSection?.section_id;
      const normalizedId = nextActiveId ? String(nextActiveId) : null;
      if (normalizedId && normalizedId !== String(lastPlaybackSectionIdRef.current)) {
        lastPlaybackSectionIdRef.current = normalizedId;
        const lock = manualSectionLockRef.current;
        if (lock?.active) {
          if (String(lock.lockedId || "") === normalizedId) {
            manualSectionLockRef.current = { active: false, lockedId: null };
          } else {
            manualSectionLockRef.current = { active: false, lockedId: null };
            activeSectionRef.current = normalizedId;
            setActiveSectionId(normalizedId);
            console.log("[section-sync] active", { sectionId: normalizedId, label: currentSection?.label || currentSection?.section_label, beatsElapsed });
          }
          return;
        }
      }
      if (normalizedId && normalizedId !== String(activeSectionRef.current) && !manualSectionLockRef.current?.active) {
        activeSectionRef.current = normalizedId;
        setActiveSectionId(normalizedId);
        console.log("[section-sync] active", { sectionId: normalizedId, label: currentSection?.label || currentSection?.section_label, beatsElapsed });
      }
    };

    console.log("[section-sync] start", {
      bpm,
      beatsPerMeasure,
      sectionStartAt,
      sectionStartBeat,
      sections: orderedSections.length,
    });
    tick();
    sectionIntervalRef.current = setInterval(tick, 120);
    return () => {
      if (sectionIntervalRef.current) {
        clearInterval(sectionIntervalRef.current);
        sectionIntervalRef.current = null;
      }
      console.log("[section-sync] stop");
    };
  }, [guideData, guideData?.beatsPerMeasure, guideData?.beats_per_measure, isTimelinePlaybackActive, sectionStartAt, sectionStartBeat]);

  useEffect(() => {
    if (!activeSectionId) return;
    if (!isTimelinePlaybackActive) {
      console.log("[section-sync] skip scroll: not playing");
      return;
    }
    if (!guideData?.sections?.length) {
      console.log("[section-sync] skip scroll: no sections");
      return;
    }
    if (sectionScrollTimeoutRef.current) {
      clearTimeout(sectionScrollTimeoutRef.current);
      sectionScrollTimeoutRef.current = null;
    }
    const container = getScrollContainer();
    const sectionEl = sectionRefs.current[String(activeSectionId)];
    if (!container || !sectionEl) {
      console.log("[section-sync] skip scroll: missing refs", { hasContainer: !!container, hasSection: !!sectionEl });
      return;
    }
    sectionScrollTimeoutRef.current = setTimeout(() => {
      const containerRect = container.getBoundingClientRect();
      const sectionRect = sectionEl.getBoundingClientRect();
      const buffer = 80;
      const targetTop = container.scrollTop + (sectionRect.top - containerRect.top) - buffer;
      const safeTop = Math.max(targetTop, 0);
      container.scrollTo({
        top: safeTop,
        behavior: "smooth",
      });
      console.log("[section-sync] scrollTo", { sectionId: activeSectionId, targetTop: safeTop });
      sectionScrollTimeoutRef.current = null;
    }, 250);
    return () => {
      if (sectionScrollTimeoutRef.current) {
        clearTimeout(sectionScrollTimeoutRef.current);
        sectionScrollTimeoutRef.current = null;
      }
    };
  }, [activeSectionId, getScrollContainer, guideData?.sections, isTimelinePlaybackActive]);

  useEffect(() => {
    setPlaybackMode(null);
  }, [currentArrangement?.id]);

  useEffect(() => {
    if (hasPlayableStems) {
      setPlaybackMode((prev) => (prev ? prev : "stems"));
      return;
    }
    if (hasGuideStems === false || !hasGuideStems) {
      setPlaybackMode("lite");
    }
  }, [hasPlayableStems, hasGuideStems]);

  useEffect(() => {
    if (effectivePlaybackMode === "lite" && stemPlayer.isPlaying) {
      stemPlayer.stop?.();
    }
  }, [effectivePlaybackMode, stemPlayer.isPlaying, stemPlayer.stop]);

  useEffect(() => {
    if (guideMissingMeasures && guideState.isPlaying) {
      setGuideState((prev) => ({ ...prev, isPlaying: false }));
      setAccentGate(null);
    }
  }, [guideMissingMeasures, guideState.isPlaying, setAccentGate]);
  useEffect(() => {
    if (tracksEnabled) return;
    stopFollowerPlayback({ keepActiveSection: true });
    stemPlayer.setExpanded(false);
  }, [stopFollowerPlayback, stemPlayer, tracksEnabled]);

  useEffect(() => {
    const storageKey = "ct-transport-state";
    if (transportSyncTimerRef.current) {
      clearInterval(transportSyncTimerRef.current);
      transportSyncTimerRef.current = null;
    }
    if (!isTimelinePlaybackActive || typeof sectionStartAt !== "number") {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            isLitePlaybackActive: false,
            updatedAtMs: Date.now(),
          }),
        );
      } catch (_) {
        // ignore storage failures
      }
      return;
    }

    const ctx = getSharedAudioContext();
    const writeState = () => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            isLitePlaybackActive: true,
            transportStartAt,
            sectionStartAt,
            sectionStartBeat,
            audioTimeSec: ctx.currentTime,
            updatedAtMs: Date.now(),
          }),
        );
      } catch (_) {
        // ignore storage failures
      }
    };

    writeState();
    transportSyncTimerRef.current = setInterval(writeState, 250);
    return () => {
      if (transportSyncTimerRef.current) {
        clearInterval(transportSyncTimerRef.current);
        transportSyncTimerRef.current = null;
      }
    };
  }, [isTimelinePlaybackActive, transportStartAt, sectionStartAt, sectionStartBeat]);
  const goToDisplay = () => {
    if (!selectedService) {
      setError("Select a service to open Display Mode");
      return;
    }
    navigate(`/display?setlist=${selectedService}`);
  };
  useEffect(() => {
    // stop pad when switching selected song
    padPlayer.stopPad?.();
  }, [selectedSong?.id, selectedSong?.songid]);
  useEffect(() => {
    const initSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        setUser(data.session.user);
        fetchUsername(data.session.user.id);
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    };

    initSession();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const authUser = session?.user ?? null;
      setUser(authUser);
      if (authUser) fetchUsername(authUser.id);
      setAuthLoading(false);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const fetchUsername = async (uid) => {
    const { data, error } = await supabase.from("users").select("username").eq("supabase_uid", uid).maybeSingle();
    if (!error && data?.username) {
      setUsername(data.username);
    }
  };
  useEffect(() => {
    if (selectedBand && user) {
      const fetchBandDefault = async () => {
        const { data, error } = await supabase.from("bands").select("default_service_title").eq("id", selectedBand).maybeSingle();

        if (error) {
          setError("Failed to fetch band details: " + error.message);
        } else if (data?.default_service_title != null) {
          setNewServiceTitle(data.default_service_title || "");
        }
      };
      fetchBandDefault();
      fetchServices(selectedBand);
      checkIsLeader(selectedBand).then(setIsLeader);
    }
  }, [selectedBand]);
  useEffect(() => {
    if (user && selectedBand) {
      const check = async () => {
        setLeaderCheckDone(false); // start loading state
        const leader = await checkIsLeader(selectedBand);
        setIsLeader(leader);
        setLeaderCheckDone(true); // finished checking
      };
      check();
    } else {
      setIsLeader(false);
      setLeaderCheckDone(true);
    }
  }, [user, selectedBand]);

  const [newServiceTitle, setNewServiceTitle] = useState("");
  const [newServiceDate, setNewServiceDate] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [allSongs, setAllSongs] = useState([]);
  const [songSearch, setSongSearch] = useState("");
  const [showSongForm, setShowSongForm] = useState(false);
  const [editingSong, setEditingSong] = useState(null); // null for create, song object for edit
  const [songFormData, setSongFormData] = useState({
    title: "",
    artist: "",
    skey: "",
    url: "",
    chordpro: "",
    spotify: "",
    track: "",
  });
  const [rememberMe, setRememberMe] = useState(true);
  const updateSelectedBand = useCallback((value) => {
    const normalizedValue = normalizeSelectionValue(value);
    selectedBandRef.current = normalizedValue;
    setSelectedBand(normalizedValue);
  }, []);
  const updateSelectedService = useCallback((value) => {
    const normalizedValue = normalizeSelectionValue(value);
    selectedServiceRef.current = normalizedValue;
    setSelectedService(normalizedValue);
  }, []);
  useEffect(() => {
    selectedBandRef.current = normalizeSelectionValue(selectedBand);
    if (user?.id) {
      writeStoredSelectedBand(user.id, selectedBand);
    }
  }, [selectedBand, user?.id]);
  useEffect(() => {
    selectedServiceRef.current = normalizeSelectionValue(selectedService);
  }, [selectedService]);

  // Notes canvas (new implementation)
  const canvasRef = useRef(null); // override or reuse the existing one if present
  const ctxRef = useRef(null);
  const isDrawing = useRef(false);
  // helper key; used to store image data per song
  const notesKey = useMemo(() => {
    if (!selectedSong) return null;
    const id = selectedSong.id || selectedSong.songid || selectedSong.songId || selectedSong.ID;
    return id ? `chord_notes_${id}` : null;
  }, [selectedSong?.id, selectedSong?.songid, selectedSong?.songId, selectedSong?.ID]);

  const checkIsLeader = async (bandId) => {
    if (!user?.id || !bandId) return false;

    const { data, error: err } = await supabase.from("user_bands").select("role").eq("user_id", user.id).eq("band_id", bandId).maybeSingle();

    if (err) return false;

    return data?.role === "leader";
  };
  useEffect(() => {
    if (!user) return;
    fetchBands();
  }, [user]);

  const fetchBands = async () => {
    const requestId = ++fetchBandsRequestIdRef.current;
    const preferredBandId = selectedBandRef.current || readStoredSelectedBand(user?.id);

    // Try loading from cache first for instant display
    try {
      const cachedBands = await getCachedBands();
      if (cachedBands && cachedBands.length > 0) {
        if (requestId !== fetchBandsRequestIdRef.current) return;
        setBands(cachedBands);
        const cachedSelection = resolveBandSelection(cachedBands, preferredBandId);
        if (!isSameSelection(selectedBandRef.current, cachedSelection)) {
          updateSelectedBand(cachedSelection);
        }
      }
    } catch (cacheError) {}

    // Then try fetching fresh data from server
    try {
      const { data, error: err } = await withTimeout(
        supabase
          .from("user_bands")
          .select(
            `
            band_id,
            role,
            bands!user_bands_band_id_fkey (
              id,
              name,
              description
            )
          `,
          )
          .eq("user_id", user.id),
        8000,
        "fetchBands",
      );

      if (err) throw err;
      if (requestId !== fetchBandsRequestIdRef.current) return;

      const bandList = data.map((row) => ({
        id: row.bands.id,
        name: row.bands.name,
        description: row.bands.description,
        role: row.role,
      }));

      setBands(bandList);

      const newSelectedBand = resolveBandSelection(bandList, selectedBandRef.current || preferredBandId);
      if (!isSameSelection(selectedBandRef.current, newSelectedBand)) {
        updateSelectedBand(newSelectedBand);
      }

      // Cache the bands
      await cacheBands(bandList);
    } catch (err) {
      if (err.message && err.message.includes("timeout")) {
      } else {
        setError("Failed to fetch bands: " + err.message);
      }
    }
  };
  const fetchServices = async (bandIdOverride = selectedBandRef.current) => {
    const bandId = normalizeSelectionValue(bandIdOverride);
    if (!bandId) {
      setServices([]);
      updateSelectedService("");
      return;
    }
    const requestId = ++fetchServicesRequestIdRef.current;

    // Try loading from cache first
    try {
      const cachedServices = await getCachedServices(bandId);
      if (cachedServices && cachedServices.length > 0) {
        if (requestId !== fetchServicesRequestIdRef.current || !isSameSelection(selectedBandRef.current, bandId)) return;
        const orderedCachedServices = [...cachedServices].reverse();
        setServices(orderedCachedServices);
        const cachedSelection = resolveServiceSelection(orderedCachedServices, selectedServiceRef.current, bandId);
        if (!isSameSelection(selectedServiceRef.current, cachedSelection)) {
          updateSelectedService(cachedSelection);
        }
      }
    } catch (cacheError) {}

    // Then try fetching fresh data from server
    try {
      const { data, error: err } = await withTimeout(supabase.from("services").select("id, servicetitle, sdate, band_id").eq("band_id", bandId).order("sdate", { ascending: false }).limit(20), 8000, "fetchServices");

      if (err) throw err;
      if (requestId !== fetchServicesRequestIdRef.current || !isSameSelection(selectedBandRef.current, bandId)) return;

      if (!data || data.length === 0) {
        setServices([]);
        updateSelectedService("");
        return;
      }

      setServices(data);

      const newSelectedService = resolveServiceSelection(data, selectedServiceRef.current, bandId);
      if (!isSameSelection(selectedServiceRef.current, newSelectedService)) {
        updateSelectedService(newSelectedService);
      }

      // Cache the services
      await cacheServices(data, bandId);
    } catch (err) {
      if (err.message && err.message.includes("timeout")) {
      } else {
        setError("Failed to load services: " + err.message);
      }
    }
  };

  const createService = async () => {
    if (!isLeader) {
      setError("Only leaders can create services");
      return;
    }

    if (!newServiceDate || !newServiceTitle.trim()) {
      setError("Please enter a date and title");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("services")
        .insert({
          band_id: selectedBand,
          sdate: newServiceDate,
          servicetitle: newServiceTitle.trim(),
        })
        .select(); // ← Returns array: data: [newRow]

      if (error) throw error;
      if (!data || data.length === 0) throw new Error("No data returned from insert");

      const newService = data[0]; // ← Extract first (and only) row

      // Refresh services list
      await fetchServices();

      // Auto-select the new service
      updateSelectedService(newService.ID?.toString() || newService.id?.toString() || "");

      // Reset form
      setNewServiceDate("");
      setNewServiceTitle("");
      setShowEditModal(false);
    } catch (err) {
      setError("Failed to create service: " + err.message);
    }
  };

  const fetchSongs = async (options = {}) => {
    const { forceRefresh = false } = options;
    if (!selectedService && selectedBand) {
      const { data: latest } = await supabase.from("services").select("id").eq("band_id", selectedBand).lte("sdate", new Date().toISOString().split("T")[0]).order("sdate", { ascending: false }).limit(1).maybeSingle();

      if (latest) {
        updateSelectedService(latest.id);
      } else {
        setSongs([]);
        return;
      }
    }

    let cachedSetlist = null;

    if (!forceRefresh) {
      try {
        cachedSetlist = await getCachedSetlist(selectedService);
        if (cachedSetlist && cachedSetlist.length > 0) {
          const formattedSongs = cachedSetlist.map((item) => ({
            ss_id: item.id,
            songid: String(item.songid),
            title: item.songs?.title || "Unknown",
            artist: item.songs?.artist || "",
            skey: item.custom_key || item.songs?.skey || "C",
            url: item.songs?.url || "",
            chordpro: item.songs?.chordpro || "",
            spotify: item.songs?.spotify || "",
            track: item.songs?.track || "",
            band_id: item.songs?.band_id || item.band_id || null,
            order: item.order,
          }));

          setSongs(formattedSongs);
          return;
        }
      } catch (cacheError) {
        cachedSetlist = null;
      }
    }

    try {
      const { data, error: err } = await withTimeout(
        supabase
          .from("servicessongs")
          .select(
            `
            id,
            order,
            custom_key,
            songid,
            songs!inner (
              id,
              title,
              artist,
              skey,
              url,
              chordpro,
              spotify,
              track,
              band_id
            )
          `,
          )
          .eq("serviceid", selectedService)
          .order("order", { ascending: true }),
        forceRefresh ? 8000 : 4000,
        "setlist fetch",
      );

      if (!err && data) {
        const formattedSongs = data.map((item) => ({
          ss_id: item.id,
          songid: String(item.songid),
          title: item.songs.title,
          artist: item.songs.artist,
          skey: item.custom_key || item.songs.skey,
          url: item.songs.url,
          chordpro: item.songs.chordpro,
          spotify: item.songs.spotify,
          track: item.songs.track,
          band_id: item.songs.band_id || null,
          order: item.order,
        }));

        setSongs(formattedSongs);
        await cacheSetlist(selectedService, data);
        return;
      }

      throw err;
    } catch (err) {
      if (!cachedSetlist) {
        setSongs([]);
        setError("No cached songs available offline");
      }
    }
  };
  const fetchAllSongs = async () => {
    if (!selectedBand) return;

    const { data, error: err } = await supabase.rpc("get_band_song_library", { p_band_id: selectedBand });

    if (err) {
      setError("Failed to load song library: " + err.message);
      return;
    }

    if (!data || data.length === 0) {
      setAllSongs([]);
      return;
    }

    const formatted = data.map((row) => ({
      id: row.song_id, // ← USE `id` (lowercase)
      band_id: row.band_id ?? selectedBand,
      title: row.title,
      artist: row.artist,
      skey: row.skey,
      url: row.url,
      chordpro: row.chordpro,
      spotify: row.spotify,
      track: row.track,
    }));

    setAllSongs(formatted);
  };

  const buildCachedSetlistFromSongs = (songList = []) =>
    songList.map((song, index) => ({
      id: song.ss_id || `local-${song.songid}-${index}`,
      order: song.order ?? index + 1,
      custom_key: song.skey,
      songid: song.songid,
      songs: {
        id: song.songid,
        title: song.title,
        artist: song.artist,
        skey: song.skey,
        url: song.url,
        chordpro: song.chordpro,
        spotify: song.spotify,
        track: song.track,
        band_id: song.band_id || song.bandId || null,
      },
    }));

  const normalizeSetlistOrder = (songList = []) =>
    songList.map((song, index) => ({
      ...song,
      order: index + 1,
    }));

  const persistSetlistOrder = async (songList = []) => {
    const updates = songList.flatMap((song, index) => {
      if (!song?.ss_id || String(song.ss_id).startsWith("local-")) {
        return [];
      }
      return [
        supabase
          .from("servicessongs")
          .update({ order: index + 1 })
          .eq("id", song.ss_id),
      ];
    });

    const results = await Promise.all(updates);
    const failed = results.find((result) => result.error);
    if (failed?.error) {
      throw failed.error;
    }
  };

  const cacheSetlistFromState = async (songList) => {
    if (!selectedService) return;
    try {
      await cacheSetlist(selectedService, buildCachedSetlistFromSongs(songList));
    } catch (_) {
      // ignore cache failures
    }
  };

  const addSongToSetlist = async (songId) => {
    if (!selectedService || !songId) {
      return;
    }
    if (songs.some((song) => String(song.songid) === String(songId))) {
      setError("Song already in setlist");
      return;
    }
    if (pendingSetlistSongIds[String(songId)]) return;
    setPendingSetlistSongIds((prev) => ({ ...prev, [String(songId)]: true }));

    const songMeta = allSongs.find((song) => String(song.id) === String(songId));
    const normalizedSongs = normalizeSetlistOrder(songs);
    const nextOrder =
      normalizedSongs.reduce((maxOrder, song) => {
        const order = Number(song.order);
        return Number.isFinite(order) ? Math.max(maxOrder, order) : maxOrder;
      }, 0) + 1;
    const optimistic = {
      ss_id: `local-${songId}-${Date.now()}`,
      songid: String(songId),
      title: songMeta?.title || "New Song",
      artist: songMeta?.artist || "",
      skey: songMeta?.skey || "C",
      url: songMeta?.url || "",
      chordpro: songMeta?.chordpro || "",
      spotify: songMeta?.spotify || "",
      track: songMeta?.track || "",
      band_id: songMeta?.band_id || null,
      order: nextOrder,
    };
    const optimisticSongs = [...normalizedSongs, optimistic];
    setSongs(optimisticSongs);
    cacheSetlistFromState(optimisticSongs);

    const { error } = await supabase.from("servicessongs").insert({
      serviceid: selectedService,
      songid: songId,
      order: nextOrder, // Append to end
    });

    if (error) {
      setError("Added locally; sync when online.");
    } else {
      await fetchSongs({ forceRefresh: true }); // refresh with server IDs
    }

    setSongSearch("");
    setAllSongs((prev) => [...prev]); // Trigger re-render showing all songs again
    setPendingSetlistSongIds((prev) => {
      const next = { ...prev };
      delete next[String(songId)];
      return next;
    });
  };

  // ===========================
  // AUTO SCROLLING FEATURE
  // ===========================
  const scrollActive = useRef(false);
  const scrollTimer = useRef(null);
  const durationRef = useRef(300000); // 5 minutes
  const wasScrolling = useRef(false);
  const resumeTimeout = useRef(null);
  const lastFetchedService = useRef(null);

  const startScroll = () => {
    const play = document.querySelector(".startscroll");
    const stop = document.querySelector(".stopscroll");
    const container = getScrollContainer();

    if (!container) return;

    const totalHeight = container.scrollHeight - container.clientHeight;
    const startY = container.scrollTop;
    const remaining = totalHeight - startY;
    const duration = (1 - startY / totalHeight) * durationRef.current || durationRef.current;

    if (remaining <= 0) return;

    scrollActive.current = true;
    if (play && stop) {
      play.style.display = "none";
      stop.style.display = "inline-block";
    }

    const startTime = performance.now();

    const step = (now) => {
      if (!scrollActive.current) return;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollTop = startY + remaining * progress;

      if (progress < 1) {
        scrollTimer.current = requestAnimationFrame(step);
      } else {
        stopScroll();
      }
    };

    scrollTimer.current = requestAnimationFrame(step);
  };

  const stopScroll = () => {
    const play = document.querySelector(".startscroll");
    const stop = document.querySelector(".stopscroll");
    if (scrollTimer.current) cancelAnimationFrame(scrollTimer.current);
    scrollActive.current = false;
    if (play && stop) {
      play.style.display = "inline-block";
      stop.style.display = "none";
    }
  };

  useEffect(() => {
    const container = getScrollContainer();
    if (!container) {
      return;
    }

    const handleManual = (e) => {
      if (e.target.closest("#toggleNavButton, #nextbutton, #ytbutton, #lockButton, #toggleNotesButton, #chordDrawerToggle, #followLeaderButton, .startscroll, .stopscroll")) {
        // Stop autoscroll immediately so the button tap is not swallowed
        if (scrollActive.current) stopScroll();
        // Let the button click continue normally
        return;
      }

      // Intentionally avoid manually forcing wheel scroll to keep native scroll feel.

      if (scrollActive.current) {
        stopScroll();
        clearTimeout(resumeTimeout.current);

        // 🕒 After user stops interacting for 2 seconds, resume auto-scroll
        resumeTimeout.current = setTimeout(() => {
          startScroll();
        }, 2000);
      }
    };
    // Extra patch for iOS/Android: ensure first tap on buttons triggers immediately
    const handleImmediateButtonTap = (e) => {
      const button = e.target.closest("#toggleNavButton, #nextbutton, #ytbutton, #lockButton, #toggleNotesButton, #chordDrawerToggle, #followLeaderButton, .startscroll, .stopscroll");

      if (scrollActive.current && button) {
        stopScroll(); // stop autoscroll immediately

        // Force the button's click to fire immediately on this touchstart
        // because otherwise Safari will swallow the tap as "scroll stop"
        button.click();

        // Prevent this touch from being interpreted as a scroll gesture
        e.preventDefault();
      }
    };

    container.addEventListener("touchstart", handleImmediateButtonTap, {
      passive: false,
      capture: true,
    });

    container.addEventListener("touchstart", handleManual, { passive: true, capture: true });
    container.addEventListener("wheel", handleManual, { passive: true, capture: true });
    const onScroll = () => {
      const now = Date.now();
      if (now - scrollLogThrottleRef.current > 500) {
        scrollLogThrottleRef.current = now;
      }
    };
    container.addEventListener("scroll", onScroll);

    return () => {
      clearTimeout(resumeTimeout.current);
      container.removeEventListener("touchstart", handleManual);
      container.removeEventListener("wheel", handleManual);
      container.removeEventListener("touchstart", handleImmediateButtonTap);
      container.removeEventListener("scroll", onScroll);
    };
  }, [showModal, getScrollContainer, activeSectionId, clickTrack.isClickPlaying, guideState.isPlaying]);

  useEffect(() => {
    if (showSongForm) {
      if (editingSong) {
        setSongFormData({
          title: editingSong.title || "",
          artist: editingSong.artist || "",
          skey: editingSong.skey || "",
          url: editingSong.url || "",
          chordpro: editingSong.chordpro || "",
          spotify: editingSong.spotify || "",
          track: editingSong.track || editingSong.url || "",
        });
      } else {
        const templateChordPro = `{title: }\n{key: }\n{time: }\n{tempo: }\n{duration: }\n\n`;
        setSongFormData({
          title: "",
          artist: "",
          skey: "",
          url: "",
          chordpro: templateChordPro,
          spotify: "",
          track: "",
        });
      }
    }
  }, [showSongForm, editingSong]);

  const openSongForm = (song = null) => {
    setEditingSong(song);
    setShowSongForm(true);
  };

  const parseDirective = (text = "", name) => {
    const re = new RegExp(`\\{\\s*${name}\\s*:\\s*([^}]+)\\}`, "i");
    const match = text.match(re);
    return match ? match[1].trim() : "";
  };

  const validateYouTubeId = (value) => {
    if (!value) return false;
    if (/https?:\/\//i.test(value) || value.includes("youtu")) return false;
    return /^[a-zA-Z0-9_-]{5,}$/i.test(value);
  };

  const handleSongSubmit = async (e) => {
    e.preventDefault();

    // Leader-only check
    if (isLeader === false) {
      setError("Only leaders can edit or create songs");
      return;
    }

    if (!songFormData.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!songFormData.skey) {
      setError("Please select a key.");
      return;
    }
    if (!validateYouTubeId(songFormData.url.trim())) {
      setError("YouTube URL must be the video ID only (no full link).");
      return;
    }
    if (!songFormData.chordpro.trim()) {
      setError("ChordPro content is required.");
      return;
    }
    const chordTitle = parseDirective(songFormData.chordpro, "title");
    const chordKey = parseDirective(songFormData.chordpro, "key");
    const chordTime = parseDirective(songFormData.chordpro, "time");
    if (!chordTitle || !chordKey || !chordTime) {
      setError("ChordPro must include {title:}, {key:}, and {time:} directives with values.");
      return;
    }
    const chordTempo = parseDirective(songFormData.chordpro, "tempo");
    const chordDuration = parseDirective(songFormData.chordpro, "duration");
    if (!chordTempo) {
    }
    if (!chordDuration) {
    }

    try {
      const data = {
        title: songFormData.title,
        artist: songFormData.artist,
        skey: songFormData.skey,
        url: songFormData.url,
        chordpro: songFormData.chordpro,
        spotify: songFormData.spotify,
        track: songFormData.track || songFormData.url,
      };

      let error;

      if (editingSong) {
        const songId = editingSong?.songid || editingSong?.id || editingSong?.ID;
        const editingBandId = getSongBandId(editingSong, selectedBandRef.current);
        let updateQuery = supabase.from("songs").update(data).eq("id", songId);
        if (editingBandId) {
          updateQuery = updateQuery.eq("band_id", editingBandId);
        }
        ({ error } = await updateQuery);

        // Update selected song if open
        if (selectedSong && (selectedSong.id === songId || selectedSong.songid === songId)) {
          setSelectedSong((prev) => (prev ? { ...prev, ...data } : prev));
        }
      } else {
        ({ error } = await supabase
          .from("songs")
          .insert({ ...data, band_id: selectedBand })
          .select());
      }

      if (error) throw error;

      setShowSongForm(false);
      setSongFormData({
        title: "",
        artist: "",
        skey: "",
        url: "",
        chordpro: "",
        spotify: "",
        track: "",
      });
      setEditingSong(null);

      await fetchAllSongs();
      await fetchSongs();
    } catch (err) {
      setError("Failed to save song: " + err.message);
    }
  };
  useEffect(() => {
    if (selectedSong?.skey) {
      setChordKey(selectedSong.skey);
    }
    // Only trigger when a *different song* is selected
  }, [selectedSong?.id]);
  useEffect(() => {
    if (!guideState.isPlaying && sectionScrollTimeoutRef.current) {
      clearTimeout(sectionScrollTimeoutRef.current);
      sectionScrollTimeoutRef.current = null;
    }
  }, [guideState.isPlaying]);

  useEffect(() => {
    setActiveSectionId(null);
    setSectionStartAt(null);
    setSectionStartBeat(0);
  }, [selectedSong?.id, selectedSong?.songid]);

  useEffect(() => {
    if (!guideState.isPlaying && !selectedLeaderId) {
      setActiveSectionId(null);
      setSectionStartAt(null);
      setSectionStartBeat(0);
    }
  }, [guideState.isPlaying, selectedLeaderId]);
  useEffect(() => {
    if (selectedSong) {
      stopScroll(); // Stop auto-scroll on any song change

      const match = /\{duration:(\d+)\}/.exec(selectedSong.chordpro);
      durationRef.current = match ? parseInt(match[1], 10) * 1000 : 300000;

      const container = getScrollContainer();
      if (container) {
        setTimeout(() => {
          container.scrollTop = 0;
        }, 0); // Ensure reset after render queue
      }
    }
  }, [selectedSong, getScrollContainer]);

  useEffect(() => {
    if (user && selectedBand && selectedService && lastFetchedService.current !== selectedService) {
      lastFetchedService.current = selectedService;
      fetchSongs();
    }
  }, [user, selectedBand, selectedService]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const nav = document.getElementById("songnav");
      const toggleButton = document.getElementById("toggleNavButton");
      if (nav && nav.style.display !== "none" && !nav.contains(e.target) && (!toggleButton || !toggleButton.contains(e.target))) {
        toggleNav();
      }
    };

    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("contextmenu", (e) => {
      if (e.target.classList.contains("notes-canvas")) e.preventDefault();
    });
    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // Supabase doesn't need explicit persistence—it's handled via storage
      const { data, error: err } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (err) throw err;
      setUser(data.user);
      // Optional: If !rememberMe, you could implement session-only by clearing on window close, but skip for now
    } catch (err) {
      setError(err.message || "Login failed");
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      updateSelectedBand("");
      updateSelectedService("");
      // Clear other states as needed
    } catch (err) {
      setError("Logout failed: " + err.message);
    }
  };

  const withTimeout = async (promise, ms, label) => {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(`${label} timeout`)), ms);
    });
    try {
      return await Promise.race([promise, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const fetchSongAndSectionsOnline = async (songId, options = {}) => {
    const { timeoutMs = 5000, bandId = null } = options;
    const normalizedBandId = normalizeSelectionValue(bandId);
    let songQuery = supabase.from("songs").select("*").eq("id", songId);
    if (normalizedBandId) {
      songQuery = songQuery.eq("band_id", normalizedBandId);
    }
    const songFetchPromise = songQuery.single();
    const sectionsFetchPromise = supabase.from("song_sections").select("*").eq("song_id", songId).order("section_order", { ascending: true });
    const [songRes, sectionsRes] = await withTimeout(Promise.all([songFetchPromise, sectionsFetchPromise]), timeoutMs, "song data fetch");
    return { songRes, sectionsRes };
  };

  const refreshSelectedSongData = async (songIdOverride = null) => {
    const songId = songIdOverride || selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
    if (!songId) return;
    const songBandId = getSongBandId(selectedSong, selectedBandRef.current);
    try {
      const { songRes, sectionsRes } = await fetchSongAndSectionsOnline(songId, { timeoutMs: 8000, bandId: songBandId });

      if (!songRes.error && songRes.data) {
        const freshData = songRes.data;
        setSelectedSong((prev) => {
          if (!prev) return freshData;
          return {
            ...prev,
            ...freshData,
            id: freshData.id || prev.id,
            songid: freshData.id || prev.songid,
          };
        });
      }

      if (!sectionsRes.error && sectionsRes.data) {
        setAvailableSections(sectionsRes.data);
        await cacheSongSections(songId, sectionsRes.data);
        await loadArrangement(songId, sectionsRes.data, {
          preferCache: true,
          forceRefresh: true,
          bandId: getSongBandId(songRes.data || selectedSong, songBandId),
          serviceId: selectedServiceRef.current,
        });
      }
    } catch (err) {
      setError("Refresh failed (offline?)");
    }
  };

  const refreshActiveData = async () => {
    if (isManualRefreshRunning) return;
    setIsManualRefreshRunning(true);
    try {
      const tasks = [];
      if (selectedService) tasks.push(fetchSongs({ forceRefresh: true }));
      if (selectedSong) tasks.push(refreshSelectedSongData());
      await Promise.all(tasks);
    } catch (err) {
      setError("Refresh failed (offline?)");
    } finally {
      setIsManualRefreshRunning(false);
    }
  };

  // ===========================
  // ARRANGEMENT MANAGEMENT
  // ===========================

  const loadArrangement = async (songId, allSections, options = {}) => {
    const { preferCache = false, forceRefresh = false, bandId: bandIdOverride = null, serviceId: serviceIdOverride = null } = options;
    const arrangementBandId = normalizeSelectionValue(bandIdOverride ?? getSongBandId(selectedSong, selectedBandRef.current));
    const arrangementServiceId = normalizeSelectionValue(serviceIdOverride ?? selectedServiceRef.current) || null;
    const tryCachedArrangement = async () => {
      if (!arrangementBandId) return false;
      const cachedArr = await getCachedArrangement(songId, arrangementBandId, arrangementServiceId);
      if (!cachedArr) return false;
      const cachedArrSections = await getCachedArrangementSections(cachedArr.id);
      if (!cachedArrSections || cachedArrSections.length === 0) return false;
      setCurrentArrangement(cachedArr);
      const orderedSections = cachedArrSections.map((as) => ({
        ...as.song_sections,
        arrangement_section_id: as.id,
        arrangement_order: as.section_order,
      }));
      setSections(orderedSections);
      setArrangementSections(cachedArrSections);
      return true;
    };

    let usedCache = false;
    if (preferCache || !forceRefresh) {
      try {
        usedCache = await tryCachedArrangement();
        if (usedCache && !forceRefresh) return;
      } catch (err) {
        // fall through to online
      }
    }

    const allowOnline = forceRefresh || !usedCache;
    if (!allowOnline) return;
    if (!arrangementBandId) {
      setSections(allSections || []);
      return;
    }

    if (!isLeader) {
      try {
        let arrangement = null;
        let arrSections = null;
        const bandId = arrangementBandId;
        const serviceId = arrangementServiceId;

        if (bandId) {
          const serviceQuery = supabase.from("song_arrangements").select("*").eq("song_id", songId).eq("band_id", bandId).eq("service_id", serviceId).maybeSingle();
          const defaultQuery = supabase.from("song_arrangements").select("*").eq("song_id", songId).eq("band_id", bandId).is("service_id", null).maybeSingle();

          const [serviceArrRes, defaultArrRes] = await withTimeout(Promise.all([serviceQuery, defaultQuery]), forceRefresh ? 8000 : 4000, "arrangement fetch");
          arrangement = serviceArrRes.data || defaultArrRes.data || null;
        }

        if (arrangement) {
          const { data: onlineArrSections, error: sectionsError } = await withTimeout(
            supabase
              .from("song_arrangements_sections")
              .select(
                `
                id,
                section_order,
                song_sections!inner (
                  id,
                  section_label,
                  content,
                  section_order,
                  suppress_downbeat
                )
              `,
              )
              .eq("arrangement_id", arrangement.id)
              .order("section_order", { ascending: true }),
            forceRefresh ? 8000 : 4000,
            "arrangement sections fetch",
          );

          if (!sectionsError && onlineArrSections) {
            arrSections = onlineArrSections;
            await cacheArrangement(arrangement, arrSections);
          }
        }

        if (arrangement && arrSections) {
          const orderedSections = arrSections.map((as) => ({
            ...as.song_sections,
            arrangement_section_id: as.id,
            arrangement_order: as.section_order,
          }));
          setSections(orderedSections);
          setArrangementSections(arrSections);
          setCurrentArrangement(arrangement);
          return;
        }
      } catch (err) {
        // fall through to section fallback
      }
      setSections(allSections || []);
      return;
    }

    try {
      let arrangement = null;
      let arrSections = null;

      // Try loading from Supabase (online)
      try {
        const { data: arrangementId, error: ensureError } = await withTimeout(
          supabase.rpc("ensure_default_arrangement", {
            p_song_id: songId,
            p_band_id: arrangementBandId,
          }),
          forceRefresh ? 10000 : 6000,
          "ensure arrangement",
        );

        if (ensureError) throw ensureError;

        const { data: arrangementRow, error: arrangementError } = await withTimeout(supabase.from("song_arrangements").select("*").eq("id", arrangementId).single(), forceRefresh ? 8000 : 4000, "arrangement row fetch");
        if (arrangementError) throw arrangementError;
        arrangement = arrangementRow;

        // Ensure arrangement sections align with song sections
        const { error: syncError } = await withTimeout(
          supabase.rpc("sync_arrangement_sections", {
            p_arrangement_id: arrangementId,
          }),
          forceRefresh ? 10000 : 6000,
          "sync arrangement",
        );
        if (syncError) throw syncError;

        // 2. Load arrangement sections from Supabase
        const { data: onlineArrSections, error: sectionsError } = await withTimeout(
          supabase
            .from("song_arrangements_sections")
            .select(
              `
              id,
              section_order,
              song_sections!inner (
                id,
                section_label,
                content,
                section_order,
                suppress_downbeat
              )
            `,
            )
            .eq("arrangement_id", arrangement.id)
            .order("section_order", { ascending: true }),
          forceRefresh ? 8000 : 4000,
          "arrangement sections fetch",
        );

        if (sectionsError) throw sectionsError;
        arrSections = onlineArrSections;

        // ✅ Cache arrangement to IndexedDB
        await cacheArrangement(arrangement, arrSections);
      } catch (onlineError) {
        // 📦 Offline: Try loading from IndexedDB cache
        const cachedArr = arrangementBandId ? await getCachedArrangement(songId, arrangementBandId, arrangementServiceId) : null;

        if (cachedArr) {
          arrangement = cachedArr;
          arrSections = await getCachedArrangementSections(arrangement.id);
        } else {
          throw new Error("No cached arrangement found");
        }
      }

      setCurrentArrangement(arrangement);

      // 3. Build ordered sections array
      const orderedSections = arrSections.map((as) => ({
        ...as.song_sections,
        arrangement_section_id: as.id,
        arrangement_order: as.section_order,
      }));

      setSections(orderedSections);
      setArrangementSections(arrSections);
    } catch (err) {
      // Fallback to showing all sections in default order
      setSections(allSections || []);
    }
  };

  const refreshSectionsAndArrangement = async (songIdOverride, preloadedSections = null) => {
    const normalizedSongId = songIdOverride || selectedSong?.songid || selectedSong?.id || selectedSong?.songId || selectedSong?.ID;
    if (!normalizedSongId) return;
    const songBandId = getSongBandId(selectedSong) || getSongBandId(editingSong, selectedBandRef.current);

    try {
      let updated = preloadedSections;
      if (!updated) {
        const { data, error } = await supabase.from("song_sections").select("*").eq("song_id", normalizedSongId).order("section_order", { ascending: true });
        if (error) throw error;
        updated = data || [];
      }
      setAvailableSections(updated);
      await loadArrangement(normalizedSongId, updated, { bandId: songBandId, serviceId: selectedServiceRef.current });

      // If these sections belong to the currently open song, switch to sections view
      const currentSongId = selectedSong?.songid || selectedSong?.id || selectedSong?.songId || selectedSong?.ID;
      if (currentSongId && String(currentSongId) === String(normalizedSongId)) {
        setUseMonolithicView(false);
        savePreference("useMonolithicView", false);
      }
    } catch (err) {}
  };

  const openSectionEditor = (songId) => {
    const normalizedSongId = songId || selectedSong?.songid || selectedSong?.id || selectedSong?.songId || selectedSong?.ID;
    const bandId = getSongBandId(selectedSong) || getSongBandId(editingSong) || normalizeSelectionValue(selectedBand);
    if (!normalizedSongId || !bandId) {
      setError("Missing song or band for editing sections");
      return;
    }
    setSectionEditorSongId(normalizedSongId);
    setSectionEditorBandId(bandId);
    setShowSectionEditor(true);
  };

  const handleDeleteSectionsAndArrangements = async () => {
    const songId = editingSong?.id || editingSong?.songid || editingSong?.ID;
    if (!songId) {
      setError("Missing song ID for deletion");
      return;
    }
    const confirmDelete = window.confirm(
      [
        "Delete Sections & Arrangements?",
        "",
        "All sections will be permanently deleted.",
        "All custom arrangements will be permanently deleted.",
        "The song will fall back to monolithic chordpro.",
        'Sections can be recreated later using "Create Sections".',
      ].join("\n"),
    );
    if (!confirmDelete) return;

    try {
      setError("");
      const { data: arrangementRows, error: arrangementFetchError } = await supabase.from("song_arrangements").select("id").eq("song_id", songId);
      if (arrangementFetchError) throw arrangementFetchError;

      const { error: sectionsDeleteError } = await supabase.from("song_sections").delete().eq("song_id", songId);
      if (sectionsDeleteError) throw sectionsDeleteError;

      const { error: arrangementsDeleteError } = await supabase.from("song_arrangements").delete().eq("song_id", songId);
      if (arrangementsDeleteError) throw arrangementsDeleteError;

      const arrangementIds = (arrangementRows || []).map((row) => row.id);
      if (arrangementIds.length > 0) {
        const { error: arrangementSectionsError } = await supabase.from("song_arrangements_sections").delete().in("arrangement_id", arrangementIds);
        if (arrangementSectionsError) throw arrangementSectionsError;
      }

      await clearCachedSongSections(songId);
      await clearCachedArrangementsForSong(songId);

      setAvailableSections([]);
      setSections([]);
      setArrangementSections([]);
      setCurrentArrangement(null);
      setActiveSectionId(null);
      setSectionStartAt(null);
      setSectionStartBeat(0);
    } catch (err) {
      console.error("Failed to delete sections/arrangements", err);
      setError(`Failed to delete sections/arrangements: ${err.message || err.toString()}`);
    }
  };

  const closeSectionEditor = () => {
    setShowSectionEditor(false);
    setSectionEditorSongId(null);
    setSectionEditorBandId(null);
  };

  const openArrangementEditor = () => {
    if (!isLeader) {
      setError("Only leaders can edit arrangements");
      return;
    }
    if (!availableSections || availableSections.length === 0) {
      setError("This song has no sections to arrange");
      return;
    }
    setShowArrangementEditor(true);
  };

  const addSectionToArrangement = async (sectionId, insertAtIndex = null) => {
    if (!currentArrangement) return;

    let newOrder;
    if (insertAtIndex !== null && insertAtIndex < sections.length) {
      // Insert at specific position: calculate order between surrounding items
      const prevOrder = insertAtIndex > 0 ? sections[insertAtIndex - 1].arrangement_order : 0;
      const nextOrder = sections[insertAtIndex].arrangement_order;
      newOrder = Math.floor((prevOrder + nextOrder) / 2);

      // If no room between orders, reorder everything
      if (newOrder === prevOrder || newOrder === nextOrder) {
        await reorderAllSections();
        newOrder = (insertAtIndex + 1) * 10;
      }
    } else {
      // Add to end
      newOrder = (arrangementSections.length + 1) * 10;
    }

    const { error } = await supabase.from("song_arrangements_sections").insert({
      arrangement_id: currentArrangement.id,
      section_id: sectionId,
      section_order: newOrder,
    });

    if (error) {
      setError("Failed to add section: " + error.message);
      return;
    }

    // Reload arrangement
    await loadArrangement(selectedSong.songid, availableSections);
  };

  const reorderAllSections = async () => {
    // Normalize all section orders to 10, 20, 30, etc.
    const updates = sections.map((section, index) => ({
      id: section.arrangement_section_id,
      section_order: (index + 1) * 10,
    }));

    await Promise.all(updates.map(({ id, section_order }) => supabase.from("song_arrangements_sections").update({ section_order }).eq("id", id)));
  };

  const removeSectionFromArrangement = async (arrangementSectionId) => {
    const { error } = await supabase.from("song_arrangements_sections").delete().eq("id", arrangementSectionId);

    if (error) {
      setError("Failed to remove section: " + error.message);
      return;
    }

    // Reload arrangement
    await loadArrangement(selectedSong.songid, availableSections);
  };

  const saveArrangementOrder = async (reorderedSections) => {
    if (!currentArrangement) return;

    const updates = reorderedSections.map((section, index) => ({
      id: section.arrangement_section_id,
      section_order: (index + 1) * 10,
    }));

    const updatePromises = updates.map(({ id, section_order }) => supabase.from("song_arrangements_sections").update({ section_order }).eq("id", id));

    const results = await Promise.all(updatePromises);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      setError("Failed to save arrangement: " + errors[0].error.message);
      return;
    }

    await loadArrangement(selectedSong.songid, availableSections);
  };

  const sectionsMatch = (cached = [], online = []) => {
    if (!cached || !online) return false;
    if (cached.length !== online.length) return false;
    const byId = new Map(online.map((section) => [String(section.id), section]));
    return cached.every((cachedSection) => {
      const onlineSection = byId.get(String(cachedSection.id));
      if (!onlineSection) return false;
      const cachedUpdated = cachedSection.updated_at || cachedSection.updatedAt || "";
      const onlineUpdated = onlineSection.updated_at || onlineSection.updatedAt || "";
      if (cachedUpdated && onlineUpdated && cachedUpdated !== onlineUpdated) return false;
      const fields = ["section_label", "section_order", "content", "original_key"];
      return fields.every((field) => String(cachedSection[field] ?? "") === String(onlineSection[field] ?? ""));
    });
  };

  const openChordModal = (song, options = {}) => {
    const { forceRefresh = false } = options;
    const requestId = openChordRequestIdRef.current + 1;
    openChordRequestIdRef.current = requestId;
    const isRequestActive = () => openChordRequestIdRef.current === requestId;
    try {
      // 🎵 STEP 1: Save current canvas (if any) BEFORE switching to the new song
      (async () => {
        try {
          // Save canvas notes to IndexedDB before switching songs
          const currentSongId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
          if (canvasRef.current && currentSongId) {
            const dataUrl = canvasRef.current.toDataURL();
            const dataLength = dataUrl ? dataUrl.length : 0;

            // A blank canvas is ~100-200 chars, drawings are 1000s+
            if (dataUrl && dataLength > 500) {
              await saveCanvasNotes(currentSongId, dataUrl);
            } else if (dataUrl) {
              // Still save it, but warn
              await saveCanvasNotes(currentSongId, dataUrl);
            }
          }
        } catch (saveErr) {}
      })();

      // 🛑 STEP 2: Turn off editing when switching songs
      if (isEditing) setIsEditing(false);
      if (guideState.isPlaying) {
        setGuideState((prev) => ({ ...prev, isPlaying: false }));
        setGuideStartBeat(0);
        setGuideBeatOffset(0);
        setGuideLeadInEnabled(true);
        setSectionStartAt(null);
        setSectionStartBeat(0);
        setActiveSectionId(null);
        setAccentGate(null);
        setClickBeatOffset(0);
      }

      // 🧭 STEP 3: Normalize song ID
      const songId = song?.songid || song?.id || song?.ID;
      const songBandId = getSongBandId(song, selectedBandRef.current);
      if (!songId) throw new Error("Invalid song object (no id)");

      // 🎹 STEP 4: Set selected song (normalized) and update chordKey
      const normalizedSongId = song.id || song.songid || songId;
      setSelectedSong({
        ...song,
        id: normalizedSongId, // Ensure id is always set
        songid: normalizedSongId, // normalize (as string for consistency)
        band_id: songBandId,
        custom_key: song.custom_key || song.skey,
        order: song.order,
      });

      // Sync chord key dropdown - prioritize custom_key from setlist, then song's original key
      setChordKey(song.custom_key || song.skey || data.skey || "A");

      // 🔧 STEP 5: Load user preference for monolithic view (async)
      getPreference("useMonolithicView", false)
        .then((savedPreference) => {
          if (isRequestActive()) {
            setUseMonolithicView(savedPreference);
          }
        })
        .catch(() => {});

      // 🎥 STEP 6: Reset video and modal state
      setShowVideo(false);
      setVideoLoaded(false);
      setChordDrawerOpen(false);
      setShowModal(true);

      // 🎶 STEP 7: Fetch full song data + sections (cache-first)
      (async () => {
        let cachedSections = null;
        try {
          cachedSections = await getCachedSongSections(songId);
        } catch (cacheError) {
          cachedSections = null;
        }

        if (!isRequestActive()) return;

        if (cachedSections && cachedSections.length > 0) {
          setAvailableSections(cachedSections);
          loadArrangement(songId, cachedSections, { preferCache: true, bandId: songBandId, serviceId: selectedServiceRef.current });
        } else {
          setSections([]);
          setAvailableSections([]);
        }

        if (!forceRefresh && cachedSections && cachedSections.length > 0) return;

        try {
          const { songRes, sectionsRes } = await fetchSongAndSectionsOnline(songId, { timeoutMs: forceRefresh ? 8000 : 4000, bandId: songBandId });
          if (!isRequestActive()) return;

          if (!songRes.error && songRes.data) {
            const freshData = songRes.data;
            setSelectedSong((prev) => {
              if (!prev) return freshData;
              return {
                ...prev,
                ...freshData,
                id: freshData.id || prev.id,
                songid: freshData.id || prev.songid,
              };
            });
          }

          if (!sectionsRes.error && sectionsRes.data && sectionsRes.data.length > 0) {
            const shouldUpdateCache = !cachedSections || !sectionsMatch(cachedSections, sectionsRes.data);
            if (shouldUpdateCache) {
              setAvailableSections(sectionsRes.data);
              await cacheSongSections(songId, sectionsRes.data);
              loadArrangement(songId, sectionsRes.data, {
                preferCache: true,
                forceRefresh,
                bandId: getSongBandId(songRes.data || song, songBandId),
                serviceId: selectedServiceRef.current,
              });
            } else if (cachedSections && cachedSections.length > 0) {
              loadArrangement(songId, cachedSections, { preferCache: true, bandId: songBandId, serviceId: selectedServiceRef.current });
            }
          }
        } catch (fetchError) {}
      })();
    } catch (err) {
      setError(`Failed to open song ${song?.title || "(unknown)"}: ${err.message}`);
    }
  };

  const closeChordModal = async () => {
    try {
      stemPlayer.stop?.();
      padPlayer.stopPad?.();
      clickTrack.stopClick?.();
      if (guideState.isPlaying) {
        setGuideState((prev) => ({ ...prev, isPlaying: false }));
        setGuideStartBeat(0);
        setGuideBeatOffset(0);
        setGuideLeadInEnabled(true);
        setSectionStartAt(null);
        setSectionStartBeat(0);
        setActiveSectionId(null);
        setAccentGate(null);
        setClickBeatOffset(0);
      }
      // Save current canvas for the currently selected song to IndexedDB
      const currentSongId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
      if (canvasRef.current && currentSongId) {
        try {
          const data = canvasRef.current.toDataURL();
          if (data) await saveCanvasNotes(currentSongId, data);
        } catch (e) {}
      }
    } finally {
      // Ensure editing mode is turned off
      setIsEditing(false);
      setChordDrawerOpen(false);
      setSelectedLeaderId(null);
      lastLeaderSongIdRef.current = null;
      setShowModal(false);
    }
  };
  const closePracticeModal = () => {
    practicePadPlayer.stopPad?.();
    practiceClickTrack.stopClick?.();
    setPendingPracticeClickRestart(null);
    setShowPracticeModal(false);
    setPracticeNavOpen(false);
  };
  const openSongFromPractice = (song) => {
    closePracticeModal();
    openChordModal(song);
  };
  const openPracticeFromChord = () => {
    closeChordModal();
    void fetchSongs({ forceRefresh: true });
    setShowPracticeModal(true);
  };
  const handleNextSong = () => {
    if (!selectedSong || !songs.length) {
      setError("No song selected or setlist is empty");
      return;
    }
    const currentIndex = songs.findIndex((song) => String(song.songid) === String(selectedSong.songid || selectedSong.id || selectedSong.ID));
    if (currentIndex === -1) {
      setError("Current song not found in setlist");
      return;
    }
    const nextIndex = (currentIndex + 1) % songs.length; // Wrap around using modulo
    const nextSong = songs[nextIndex];
    openChordModal(nextSong);
  };

  const toFlatNotation = (chord) => {
    const SHARP_TO_FLAT = {
      "A#": "Bb",
      "C#": "Db",
      "D#": "Eb",
      "F#": "Gb",
      "G#": "Ab",
    };

    const parsePart = (part) => {
      const match = part.match(/^([A-G])(#|b)?(.*?)$/);
      if (!match) return part;
      const root = match[1];
      const acc = match[2] || "";
      const suffix = match[3] || "";
      let note = root + acc;
      if (acc === "#") {
        note = SHARP_TO_FLAT[note] || note;
      }
      // Preserve flats (e.g., "Bb") and natural notes (e.g., "C")
      return note + suffix;
    };

    // Split and process slash chords (e.g., "G#/C" → ["G#", "C"] → ["Ab", "C"])
    const parts = chord.split("/");
    const convertedParts = parts.map(parsePart);
    return convertedParts.join("/");
  };

  const extractDirectives = (chordproText = "") => {
    const get = (name) => {
      const re = new RegExp(`\\{\\s*${name}\\s*:\\s*([^}]+)\\}`, "i");
      const m = chordproText.match(re);
      return m ? m[1].trim() : "";
    };

    // Some chordpro variants use {key: Bb} or {skey: Bb} etc.
    const title = get("title") || "";
    const keyDir = get("key") || get("skey") || "";
    const duration = get("duration") || "";
    const time = get("time") || ""; // e.g. 6/8, 4/4
    const tempo = get("tempo") || ""; // e.g. 120

    return { title, key: keyDir, duration, time, tempo };
  };

  const transposeChordPro = (chordPro, newKey, skey) => {
    try {
      // 1) Get directive key (original key the ChordPro is written in), if present
      const directives = extractDirectives(chordPro || "");
      const directiveKey = (directives.key || "").replace(/[^A-G#bm]/gi, "");

      // 2) Decide originalKey (what the text is written in) -> prefer directive, then skey, then C
      const originalKey = (directiveKey || skey || "C").replace(/[^A-G#bm]/gi, "");

      // 3) Decide target/display key -> prefer explicit newKey (UI), otherwise use skey (DB),
      //    finally fallback to originalKey (no change)
      const targetKey = newKey && newKey !== "" ? newKey.replace(/[^A-G#bm]/gi, "") : (skey || originalKey).replace(/[^A-G#bm]/gi, "");

      // 4) Normalize flats -> sharps map (same as before)
      const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
      const FLAT_EQUIVS = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
      const normalize = (k) => FLAT_EQUIVS[k] || k;
      const parseKey = (k) => {
        const match = String(k || "")
          .trim()
          .match(/^([A-Ga-g])([#b]?)(m|min)?$/i);
        if (!match) return { root: "C", isMinor: false };
        const letter = match[1].toUpperCase();
        const accidental = match[2] === "#" ? "#" : match[2] === "b" ? "b" : "";
        const isMinor = Boolean(match[3]);
        return { root: `${letter}${accidental}`, isMinor };
      };
      const toRelativeMajor = (keyObj) => {
        const normalizedRoot = normalize(keyObj.root);
        const rootIndex = NOTES.indexOf(normalizedRoot);
        if (rootIndex < 0) return "C";
        if (!keyObj.isMinor) return normalizedRoot;
        const relativeMajorIndex = (rootIndex + 3) % NOTES.length;
        return NOTES[relativeMajorIndex];
      };

      const orig = toRelativeMajor(parseKey(originalKey));
      const targ = toRelativeMajor(parseKey(targetKey));

      // 5) Compute interval (number of semitones to move up)
      const interval = (NOTES.indexOf(targ) - NOTES.indexOf(orig) + 12) % 12;

      // 6) Parse and transpose using chordsheetjs
      const parser = new ChordProParser();
      const song = parser.parse(chordPro);
      const transposedSong = song.transpose(interval);

      const formatter = new HtmlDivFormatter();
      let htmlOutput = formatter.format(transposedSong);

      // 7) Convert sharps to flats if the target key prefers flats
      const flatKeys = ["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb", "C", "Gm", "Cm", "Fm", "Bbm", "Ebm", "Abm", "Dm"];
      if (flatKeys.includes(targetKey)) {
        htmlOutput = htmlOutput.replace(/<div class="chord">([^<]+)<\/div>/gi, (match, fullChord) => {
          const converted = toFlatNotation(fullChord);
          return `<div class="chord">${converted}</div>`;
        });
      }

      // 8) Fix trailing spaces in lyrics (keeps previous behaviour)
      htmlOutput = htmlOutput.replace(/<div class="lyrics">([^<]*?)<\/div>/g, (match, text) => {
        return `<div class="lyrics">${text.replace(/ $/, "\u00A0")}</div>`;
      });

      return htmlOutput;
    } catch (err) {
      return chordPro ? chordPro.replace(/\n/g, "<br>") : "";
    }
  };

  const handleKeyChange = (e) => {
    setChordKey(e.target.value);
  };

  const handleTextSize = (increase) => {
    setFontSize((prev) => {
      const newSize = increase ? prev * 1.2 : (prev * 5) / 6;
      localStorage.setItem("chordProFontSize", newSize);
      return newSize;
    });
  };

  const toggleNav = () => {
    const nav = document.getElementById("songnav");
    if (nav) {
      nav.style.display = nav.style.display === "none" ? "flex" : "none";
    }
  };
  const togglePracticeNav = () => {
    setPracticeNavOpen((prev) => !prev);
  };

  const handleChordDrawerYoutubeToggle = useCallback(() => {
    if (!hasOnlineYouTube(selectedSong)) return;
    if (!showVideo) {
      setVideoLoaded(true);
    }
    setShowVideo((prev) => !prev);
    setChordDrawerOpen(false);
  }, [selectedSong, showVideo]);

  const handleSetlistYoutubeToggle = useCallback(
    (song) => {
      const cardId = getSetlistCardId(song);
      const embedId = getYouTubeEmbedId(song);
      if (!cardId || !song?.url || !embedId) return;
      if (setlistPlayerSongId === cardId) {
        setSetlistPlayerOpen((prev) => !prev);
        return;
      }
      setSetlistPlayerSongId(cardId);
      setSetlistPlayerEmbedId(embedId);
      setSetlistPlayerOpen(true);
    },
    [setlistPlayerSongId],
  );

  const toggleVid = (url) => {
    const iframe = document.getElementById("yt-iframe");
    if (iframe) {
      iframe.style.display = iframe.style.display === "none" ? "block" : "none";
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !selectedSong || !showModal) return;

    let cancelled = false;
    let rafId = null;
    let resizeTimeout = null;
    let restoredOnce = false;

    setIsEditing(false);

    const wrapper = canvas.parentElement; // chordpro-wrapper
    const ctx = canvas.getContext("2d", { willReadFrequently: true }); // Performance hint
    ctxRef.current = ctx;

    const q = (sel) => (wrapper ? wrapper.querySelector(sel) : null);

    const applyDrawingScale = () => {
      const ratio = window.devicePixelRatio || 1;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.strokeStyle = "yellow";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      return ratio;
    };

    let lastCssWidth = 0;
    let lastCssHeight = 0;
    let lastRatio = window.devicePixelRatio || 1;

    const measureContent = () => {
      if (!wrapper) return { widthPx: 0, heightPx: 0 };

      const contentEl = q(".chordpro-content");
      const metaEl = q(".chordpro-metadata");

      let contentWidth = wrapper.clientWidth;
      let contentHeight = wrapper.scrollHeight;

      if (contentEl) {
        const rect = contentEl.getBoundingClientRect();
        const metaH = metaEl ? metaEl.getBoundingClientRect().height : 0;

        contentWidth = Math.max(wrapper.clientWidth, Math.ceil(rect.width));
        contentHeight = Math.max(0, Math.ceil(rect.height)) + Math.ceil(metaH) + 8;
      }

      return { widthPx: contentWidth, heightPx: contentHeight };
    };

    const resizeCanvas = (preserve = true) => {
      if (cancelled || !wrapper) return null;

      const { widthPx, heightPx } = measureContent();
      const ratio = window.devicePixelRatio || 1;

      if (widthPx === lastCssWidth && heightPx === lastCssHeight && ratio === lastRatio) {
        return { widthPx, heightPx, ratio };
      }

      let snapshot = null;
      if (preserve && canvas.width > 0 && canvas.height > 0) {
        try {
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
          ctx.restore();
        } catch (e) {}
      }

      canvas.style.width = `${widthPx}px`;
      canvas.style.height = `${heightPx}px`;
      canvas.width = Math.max(1, Math.round(widthPx * ratio));
      canvas.height = Math.max(1, Math.round(heightPx * ratio));

      applyDrawingScale();

      if (snapshot) {
        try {
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.putImageData(snapshot, 0, 0);
          ctx.restore();
        } catch (e) {}
      }

      lastCssWidth = widthPx;
      lastCssHeight = heightPx;
      lastRatio = ratio;

      return { widthPx, heightPx, ratio };
    };

    const restoreSavedImage = async () => {
      if (cancelled || restoredOnce) return;

      const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
      const saved = songId ? await getCanvasNotes(songId) : null;

      if (saved) {
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          try {
            // Ensure size is locked before drawing restored pixels
            resizeCanvas(false);

            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.restore();

            applyDrawingScale();
          } catch (e) {
          } finally {
            restoredOnce = true;
          }
        };
        img.onerror = (e) => {
          restoredOnce = true;
        };
        img.src = saved;
      } else {
        restoredOnce = true;
      }
    };

    rafId = requestAnimationFrame(() => {
      resizeCanvas(false);
      restoreSavedImage();
    });

    const observer = new ResizeObserver(() => {
      if (cancelled) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCanvas(true);
      }, 120);
    });
    if (wrapper) observer.observe(wrapper);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [selectedSong?.id, selectedSong?.songid, selectedSong?.songId, selectedSong?.ID, showModal, notesKey]);

  // run each time modal opens or song changes
  // Convert page coordinates to canvas coordinates
  const getCanvasPosFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  function extractKeyFromChordPro(chordproText = "") {
    const match = chordproText.match(/\{key:\s*([A-G][b#]?)\}/i);
    if (!match) return null;
    // Normalize: uppercase note letter, lowercase accidental (e.g., "Bb", "C#", "F")
    const key = match[1];
    return key.length === 1 ? key.toUpperCase() : key[0].toUpperCase() + key[1].toLowerCase();
  }
  // start drawing
  const startDraw = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    isDrawing.current = true;
    const pos = getCanvasPosFromEvent(e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  // drawing
  const draw = (e) => {
    if (!isEditing || !isDrawing.current) return;
    e.preventDefault();
    const pos = getCanvasPosFromEvent(e);
    const ctx = ctxRef.current;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  // end drawing
  const endDraw = (e) => {
    if (!isEditing) return;
    isDrawing.current = false;
    const ctx = ctxRef.current;
    ctx.closePath();
    // persist drawing as PNG to IndexedDB (fire-and-forget)
    const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
    if (songId && canvasRef.current) {
      const data = canvasRef.current.toDataURL();
      saveCanvasNotes(songId, data).catch((err) => {});
    }
  };

  // toggle edit mode (enable/disable drawing)
  const toggleEditMode = () => {
    setIsEditing((prev) => {
      const next = !prev;
      // when entering edit mode, pause autoscroll
      if (next && scrollActive.current) {
        stopScroll();
      }
      // while editing, prevent touch scrolling by adding pointer-capture on canvas via CSS
      return next;
    });
  };

  // clear notes for this song
  const clearNotesForSong = async () => {
    const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
    if (canvasRef.current && ctxRef.current) {
      ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (songId) await clearCanvasNotes(songId);
  };

  const transposedHtml = useMemo(() => {
    if (!selectedSong || !selectedSong.chordpro) return "";
    const originalKey = extractKeyFromChordPro(selectedSong?.chordpro) || selectedSong?.skey || "C";
    const html = transposeChordPro(selectedSong.chordpro, chordKey, originalKey);
    return html;
  }, [selectedSong?.id, selectedSong?.chordpro, selectedSong?.skey, selectedSong?.url, chordKey]);

  const sectionHtmlById = useMemo(() => {
    if (!sections || sections.length === 0) return {};
    const songKeyDirective = extractKeyFromChordPro(selectedSong?.chordpro);
    const fallbackKey = songKeyDirective || selectedSong?.skey || "C";
    const htmlMap = {};
    sections.forEach((section, idx) => {
      const originalKey = section.original_key || fallbackKey;
      const sectionKey = section.id || section.section_id || section.arrangement_section_id || idx;
      const normalizedKey = String(sectionKey);
      htmlMap[normalizedKey] = transposeChordPro(section.content, chordKey, originalKey);
    });
    return htmlMap;
  }, [sections, chordKey, selectedSong?.chordpro, selectedSong?.skey]);

  // Memoize the rendered sections to prevent expensive re-transposition
  const renderedSections = useMemo(() => {
    if (!sections || sections.length === 0) return null;

    return sections.map((section, idx) => {
      const sectionKey = section.id || section.section_id || section.arrangement_section_id || idx;
      const normalizedSectionKey = String(sectionKey);
      const sectionHtml = sectionHtmlById[normalizedSectionKey] || "";
      const isActiveSection = String(activeSectionId) === normalizedSectionKey;
      const isJumpVisible = (effectivePlaybackMode === "lite" || effectivePlaybackMode === "stems") && isLeader && guideTimeline?.timeline?.ok !== false && sections?.length && isActiveSection;

      return (
        <div key={sectionKey} ref={(el) => registerSectionRef(normalizedSectionKey, el)} className={`song-section mb-4 ${isActiveSection ? "section--active" : ""}`}>
          <div className="section-heading-row mb-2" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="btn btn-outline-info section-heading-btn"
              onClick={() => {
                setActiveSectionId(normalizedSectionKey);
                if (isTimelinePlaybackActive) {
                  manualSectionLockRef.current = { active: true, lockedId: normalizedSectionKey };
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveSectionId(normalizedSectionKey);
                }
              }}
              aria-label={`Show controls for ${section.section_label}`}
            >
              <span className="section-heading-text text-info">{section.section_label}</span>
            </button>
            <span className="section-heading-action">
              <button
                type="button"
                className={`btn btn-sm btn-outline-secondary section-jump ${isJumpVisible ? "is-visible" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  requestSectionJump(normalizedSectionKey);
                  setActiveSectionId(null);
                }}
                title="Jump to section"
                tabIndex={isJumpVisible ? 0 : -1}
              >
                ▶︎
              </button>
            </span>
          </div>
          <div className="chordpro-section" dangerouslySetInnerHTML={{ __html: sectionHtml }} />
        </div>
      );
    });
  }, [sections, sectionHtmlById, activeSectionId, effectivePlaybackMode, isLeader, guideTimeline?.timeline?.ok, guideTimeline?.timeline?.sections, requestSectionJump, registerSectionRef]);

  const scrollToSectionById = useCallback(
    (sectionId) => {
      const container = getScrollContainer();
      const sectionEl = sectionRefs.current[String(sectionId)];
      if (!container || !sectionEl) return;
      const containerRect = container.getBoundingClientRect();
      const sectionRect = sectionEl.getBoundingClientRect();
      const buffer = 80;
      const targetTop = container.scrollTop + (sectionRect.top - containerRect.top) - buffer;
      container.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
    },
    [getScrollContainer],
  );

  useFollowerTransportSync({
    bandId: selectedBand,
    selectedLeaderId,
    timelineSections: guideData?.sections || [],
    measureBoundaryBeats: measureBoundaries?.boundaryArray || [],
    getSectionIndex: (sectionId) => {
      const id = String(sectionId ?? "");
      const index = sections.findIndex((section) => {
        const sectionKey = section.id ?? section.section_id ?? section.arrangement_section_id;
        return String(sectionKey) === id;
      });
      return index >= 0 ? index : null;
    },
    onSectionIndex: (nextIndex) => {
      const target = sections[nextIndex];
      const targetId = target?.id ?? target?.section_id ?? target?.arrangement_section_id;
      if (!targetId) return;
      setActiveSectionId(String(targetId));
      scrollToSectionById(String(targetId));
    },
    currentSongId: selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID,
    currentArrangementId: currentArrangement?.id || null,
    smoothScroll: true,
    leadInMeasures: 0,
    onLeaderSongChange: (payload) => {
      const leaderSongId = payload?.song_id;
      if (!leaderSongId || lastLeaderSongIdRef.current === String(leaderSongId)) return;
      lastLeaderSongIdRef.current = String(leaderSongId);
      const nextSong = songs.find((song) => String(song.songid || song.id || song.ID) === String(leaderSongId));
      if (nextSong) {
        stopFollowerPlayback();
        openChordModal(nextSong);
      }
    },
    onStopFollow: (reason) => {
      if (reason !== "leader-changed") {
        setSelectedLeaderId(null);
      }
    },
  });

  useEffect(() => {
    if (showModal && selectedLeaderId) {
      stopFollowerPlayback({ keepActiveSection: true });
    }
  }, [selectedLeaderId, showModal, stopFollowerPlayback]);

  const buildLeaderPlayPayload = useCallback(() => {
    if (!user?.id) return null;
    const bpm = Number(guideData?.bpm || guideData?.tempo);
    const beatsPerMeasureRaw = Number(guideData?.beats_per_measure || guideData?.beatsPerMeasure);
    const beatsPerMeasure = Number.isFinite(beatsPerMeasureRaw) && beatsPerMeasureRaw > 0 ? beatsPerMeasureRaw : 4;
    if (!Number.isFinite(bpm) || bpm <= 0) return null;
    if (!Number.isFinite(sectionStartAt)) return null;
    const ctx = getSharedAudioContext();
    const msPerBeat = 60000 / bpm;
    const startAtUtc = Date.now() + (sectionStartAt - ctx.currentTime) * 1000;
    const anchorUtc = startAtUtc - (Number(sectionStartBeat) || 0) * msPerBeat;
    return {
      leader_user_id: user.id,
      bpm,
      beats_per_measure: beatsPerMeasure,
      timeline_anchor_utc: anchorUtc,
    };
  }, [guideData?.beatsPerMeasure, guideData?.beats_per_measure, guideData?.bpm, guideData?.tempo, sectionStartAt, sectionStartBeat, user?.id]);

  useEffect(() => {
    if (!selectedBand || !isLeader || !user?.id) return;
    const channel = acquireBandLeaderChannel({ bandId: selectedBand });
    const unsubscribe = channel.addBroadcastListener("leader_sync_request", ({ payload }) => {
      if (payload?.leader_user_id && String(payload.leader_user_id) !== String(user.id)) return;
      const songId = selectedSong?.id || selectedSong?.songid || selectedSong?.songId || selectedSong?.ID;
      if (songId) {
        sendSongChange({
          leader_user_id: user.id,
          song_id: songId,
          arrangement_id: currentArrangement?.id || null,
          song_title: selectedSong?.title || "Untitled",
        });
      }
      if (guideState.isPlaying || clickTrack.isClickPlaying) {
        const payloadToSend = buildLeaderPlayPayload();
        if (payloadToSend) {
          sendPlay(payloadToSend);
        }
      }
    });
    return () => {
      unsubscribe();
      releaseBandLeaderChannel(selectedBand);
    };
  }, [
    buildLeaderPlayPayload,
    clickTrack.isClickPlaying,
    currentArrangement?.id,
    guideState.isPlaying,
    isLeader,
    selectedBand,
    selectedSong?.ID,
    selectedSong?.id,
    selectedSong?.songId,
    selectedSong?.songid,
    selectedSong?.title,
    sendPlay,
    sendSongChange,
    user?.id,
  ]);

  useEffect(() => {
    if (!isPresenceOnline && selectedLeaderId) {
      setSelectedLeaderId(null);
    }
  }, [isPresenceOnline, selectedLeaderId, setSelectedLeaderId]);
  useEffect(
    () => () => {
      setSelectedLeaderId(null);
    },
    [setSelectedLeaderId],
  );

  useEffect(() => {
    if (!showPracticeModal) return;
    const payload = {
      tempo: practiceTempo,
      beatsPerMeasure: practiceBeatsPerMeasure,
      suppressDownbeat: practiceSuppressDownbeat,
      padKey: practicePadKey,
      padVolume: practicePadVolume,
      padPan: practicePadPan,
      clickVolume: practiceClickVolume,
      clickPan: practiceClickPan,
    };
    savePracticeSettings(payload);
  }, [practiceBeatsPerMeasure, practiceClickPan, practiceClickVolume, practicePadKey, practicePadPan, practicePadVolume, practiceSuppressDownbeat, practiceTempo, showPracticeModal]);

  return (
    <div className="fade-in">
      {limitedMode && !showModal ? (
        <div className="limited-device-badge" aria-live="polite">
          Limited device mode{limitedReason ? ` - ${limitedReason}` : ""}
        </div>
      ) : null}
      <div className="App">
        {authLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : !user ? (
          <div className="login-container">
            <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
              <div className="card shadow-sm p-4" style={{ maxWidth: "400px", width: "100%" }}>
                <div className="text-center mb-4">
                  <h2 className="fw-bold">✝️ CrossTracks 🎵</h2>
                </div>
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required className="rounded-pill" />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="rounded-pill" />
                    <Form.Check type="checkbox" label="Remember me" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="mb-3" />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 rounded-pill">
                    Worship!
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <nav className="navbar navbar-expand-lg navbar-dark bg-dark shadow-sm">
              <div className="container-fluid">
                <div className="d-flex align-items-center w-100">
                  <a className="navbar-brand mb-0 h5 d-flex align-items-center gap-2 text-decoration-none text-light" href="/">
                    <img src="/CTWhite.svg" alt="CrossTracks logo" height="28" />
                  </a>
                  <button className="navbar-toggler ms-auto" type="button" data-bs-toggle="collapse" data-bs-target="#ct-navbar" aria-controls="ct-navbar" aria-expanded="false" aria-label="Toggle navigation">
                    <span className="navbar-toggler-icon" />
                  </button>
                </div>
                <div className="collapse navbar-collapse" id="ct-navbar">
                  <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between w-100 gap-3 py-2">
                    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2">
                      <select id="bandSelect" className="form-select form-select-sm bg-secondary text-light border-0" value={selectedBand} onChange={(e) => updateSelectedBand(e.target.value)} style={{ minWidth: "180px" }}>
                        {bands.map((band) => (
                          <option key={band.id} value={band.id}>
                            {band.name}
                          </option>
                        ))}
                      </select>
                      <Button variant="outline-info" size="sm" onClick={goToDisplay} disabled={!selectedService}>
                        Project
                      </Button>
                    </div>
                    <div className="d-flex align-items-center text-light gap-3 justify-content-lg-end">
                      <small className="text-nowrap">
                        Welcome, <strong>{username || user.email}</strong>
                      </small>
                      <Button variant="outline-light" size="sm" onClick={handleLogout} className="text-nowrap">
                        Logout
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </nav>

            {/* Leader-only controls */}
            {!leaderCheckDone ? null : isLeader ? (
              <div className="container my-4">
                <div className="card bg-light shadow-sm p-3">
                  <h5 className="text-center mb-3 text-dark fw-semibold">Create New Service</h5>
                  <div className="row g-3 align-items-center justify-content-center">
                    <div className="col-12 col-md-4">
                      <input type="text" className="form-control" placeholder="Service Title" value={newServiceTitle} onChange={(e) => setNewServiceTitle(e.target.value)} />
                    </div>
                    <div className="col-12 col-md-3">
                      <input type="date" className="form-control" value={newServiceDate} onChange={(e) => setNewServiceDate(e.target.value)} />
                    </div>
                    <div className="col-12 col-md-2">
                      <Button variant="primary" className="w-100" onClick={createService} disabled={!newServiceDate || !newServiceTitle.trim()}>
                        Create
                      </Button>
                    </div>
                    <div className="col-12 col-md-2">
                      <Button
                        variant="secondary"
                        className="w-100"
                        onClick={async () => {
                          setShowEditModal(true);
                          await fetchAllSongs();
                        }}
                        disabled={!selectedService}
                      >
                        Edit Setlist
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl" dialogClassName={showSongForm ? "dimmed-edit-modal" : ""}>
              <Modal.Header closeButton className="edit-modal-header">
                <Modal.Title className="edit-modal-title">
                  {(() => {
                    const serviceTitle = services.find((s) => s.id === parseInt(selectedService))?.servicetitle;
                    return serviceTitle ? `Edit Setlist — ${serviceTitle}` : "Edit Setlist";
                  })()}
                </Modal.Title>
                <div className="ms-auto d-flex align-items-center gap-2">
                  <Button variant="outline-light" size="sm" onClick={() => fetchSongs({ forceRefresh: true })} disabled={!selectedService || isManualRefreshRunning} title="Sync setlist from server">
                    {isManualRefreshRunning ? "Syncing…" : "Sync"}
                  </Button>
                </div>
              </Modal.Header>
              <Modal.Body className="edit-modal-body" style={{ display: "flex", height: "500px", overflow: "auto" }}>
                <DragDropContext
                  onDragEnd={async (result) => {
                    if (!result.destination) return;

                    // Optimistic update
                    const items = Array.from(songs);
                    const [reorderedItem] = items.splice(result.source.index, 1);
                    items.splice(result.destination.index, 0, reorderedItem);
                    const orderedItems = normalizeSetlistOrder(items);
                    setSongs(orderedItems);
                    cacheSetlistFromState(orderedItems);

                    const isLeader = await checkIsLeader(selectedBand);
                    if (!isLeader) {
                      setError("Only leaders can reorder songs");
                      return;
                    }

                    try {
                      await persistSetlistOrder(orderedItems);
                    } catch (err) {
                      setError("Order saved locally; sync when online.");
                    }
                  }}
                >
                  <Droppable droppableId="setlist">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="setlist-panel">
                        {songs.map((song, index) => (
                          <Draggable key={song.ss_id} draggableId={String(song.ss_id)} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="draggable-item"
                                style={{
                                  ...provided.draggableProps.style,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "8px",
                                  padding: "8px",
                                  margin: "4px 0",
                                  background: "#f8f9fa",
                                  borderRadius: "4px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span className="drag-handle" style={{ cursor: "grab" }}>
                                  ☰
                                </span>
                                <div style={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                  <span>{song.title}</span>
                                  <label style={{ marginLeft: "auto" }}>Key</label>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: "80px" }}
                                    value={song.skey}
                                    onChange={async (e) => {
                                      const newKey = e.target.value;
                                      if (!isLeader) {
                                        setError("Only leaders can change song keys");
                                        return;
                                      }

                                      if (!song.ss_id) {
                                        setError("Missing setlist song ID for key update");
                                        return;
                                      }

                                      const { error } = await supabase.from("servicessongs").update({ custom_key: newKey }).eq("id", song.ss_id);

                                      if (error) {
                                        setError("Failed to update key: " + error.message);
                                      } else {
                                        // Optimistic local update
                                        setSongs((prev) => {
                                          const updated = prev.map((s) => (s.ss_id === song.ss_id ? { ...s, skey: newKey, custom_key: newKey } : s));
                                          cacheSetlistFromState(updated);
                                          return updated;
                                        });
                                      }
                                    }}
                                  >
                                    {["Ab", "A", "Bb", "B", "C", "D", "Eb", "E", "F", "G"].map((key) => (
                                      <option key={key} value={key}>
                                        {key}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <button
                                  className="btn btn-sm btn-danger remove-btn"
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    if (!isLeader) {
                                      setError("Only leaders can remove songs");
                                      return;
                                    }

                                    const servicessongId = song.ss_id;

                                    if (!servicessongId) {
                                      setError("Cannot delete: missing servicessong ID");
                                      return;
                                    }

                                    const nextSongs = normalizeSetlistOrder(songs.filter((s) => s.ss_id !== servicessongId));
                                    setSongs(nextSongs);
                                    cacheSetlistFromState(nextSongs);

                                    const { error } = await supabase.from("servicessongs").delete().eq("id", servicessongId);

                                    if (error) {
                                      setError("Removed locally; sync when online.");
                                      return;
                                    }

                                    try {
                                      await persistSetlistOrder(nextSongs);
                                    } catch (err) {
                                      setError("Removed locally; sync when online.");
                                    }
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <div className="library-panel" style={{ width: "50%", padding: "10px", overflow: "auto" }}>
                  <div className="library-header">
                    <h5 className="mb-0">Song Library</h5>
                    {isLeader === null ? (
                      <div className="text-muted small">…</div>
                    ) : isLeader ? (
                      <Button variant="primary" size="sm" onClick={() => openSongForm()} aria-label="Add new song" title="Add new song">
                        +
                      </Button>
                    ) : null}
                  </div>

                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={songSearch}
                    onChange={(e) => setSongSearch(e.target.value)}
                    style={{
                      width: "100%",
                      marginBottom: "10px",
                      padding: "5px",
                    }}
                  />

                  {allSongs
                    .filter((song) => song.title.toLowerCase().includes(songSearch.toLowerCase()))
                    .map((song) => (
                      <div
                        key={song.id}
                        className="d-flex justify-content-between align-items-start mb-2"
                        style={{
                          borderBottom: "1px solid #ddd",
                          paddingBottom: "6px",
                          wordBreak: "break-word",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            flex: "1 1 auto",
                            marginRight: "8px",
                            whiteSpace: "normal",
                          }}
                        >
                          {song.title} ({song.skey})
                        </span>
                        <div style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                          {isLeader === null ? (
                            <div className="text-center my-4 text-muted">
                              <small>Loading permissions...</small>
                            </div>
                          ) : isLeader ? (
                            <Button variant="warning" size="sm" className="me-2" title="Edit Song" onClick={() => openSongForm(song)}>
                              ✎
                            </Button>
                          ) : null}
                          <Button
                            variant="success"
                            size="sm"
                            title="Add to Setlist"
                            disabled={Boolean(pendingSetlistSongIds[String(song.id)]) || songs.some((s) => String(s.songid) === String(song.id))}
                            onClick={async () => {
                              await addSongToSetlist(song.id); // Reuse function
                            }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </Modal.Body>
            </Modal>
            <div className="album py-5">
              <div className="container">
                <div className="d-flex justify-content-center align-items-center mb-3 gap-3 flex-wrap">
                  <h2 className="me-2 mb-0">Setlist</h2>
                  <Button variant="outline-secondary" size="sm" onClick={refreshActiveData} disabled={!selectedService || isManualRefreshRunning} title="Sync setlist from server">
                    {isManualRefreshRunning ? "Syncing…" : "Sync"}
                  </Button>
                  <select id="serviceSelect" className="form-select form-select-sm border-0 title-select" value={selectedService} onChange={(e) => updateSelectedService(e.target.value)} style={{ minWidth: "220px" }}>
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.servicetitle} ({new Date(`${service.sdate}T00:00:00`).toLocaleDateString(undefined, { timeZone: "UTC" })})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="row">
                  {songs.length > 0 ? (
                    songs.map((song) => {
                      const cardId = getSetlistCardId(song);
                      const canShowSetlistYouTube = hasOnlineYouTube(song);
                      const isActiveSetlistPlayer = setlistPlayerSongId === cardId && Boolean(setlistPlayerEmbedId);
                      return (
                        <div className="col-md-4" key={song.ss_id || song.id}>
                          <div className="card mb-4 shadow-sm setlist-song-card">
                            {/* Temporary disable YouTube preview until redesign */}
                            {/*
                        {song.url ? (
                          <img
                            src={`https://img.youtube.com/vi/${song.url}/hqdefault.jpg`}
                            alt={song.title}
                            className="card-img-top"
                            onClick={() => window.open(`https://www.youtube.com/watch?v=${song.url}`, '_blank')}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <div className="card-img-top bg-secondary text-white text-center pt-5">No Video</div>
                        )}
                        */}
                            <div className="card-body">
                              <div className="card-title d-flex align-items-center flex-wrap">
                                <span className="order-circle">{song.order}</span>
                                <strong className="ms-2">{song.title}</strong>
                              </div>
                              <div className="d-flex justify-content-end align-items-center">
                                <div className="btn-group">
                                  {canShowSetlistYouTube && (
                                    <button
                                      type="button"
                                      className={`btn btn-sm btn-outline-danger setlist-youtube-button${isActiveSetlistPlayer && setlistPlayerOpen ? " active" : ""}`}
                                      onClick={() => handleSetlistYoutubeToggle(song)}
                                      aria-label={isActiveSetlistPlayer && setlistPlayerOpen ? `Hide YouTube for ${song.title}` : `Show YouTube for ${song.title}`}
                                      title={isActiveSetlistPlayer && setlistPlayerOpen ? "Hide YouTube" : "Show YouTube"}
                                    >
                                      {YOUTUBE_BUTTON_ICON}
                                    </button>
                                  )}
                                  <button className="btn btn-sm btn-outline-dark" onClick={() => openChordModal(song)}>
                                    Chords ({song.skey})
                                  </button>
                                </div>
                              </div>
                              {isActiveSetlistPlayer && (
                                <div className={`setlist-youtube-panel${setlistPlayerOpen ? " is-open" : ""}`} aria-hidden={!setlistPlayerOpen}>
                                  <div className="setlist-youtube-frame">
                                    <iframe
                                      src={`https://www.youtube.com/embed/${setlistPlayerEmbedId}?autoplay=1`}
                                      title={`${song.title} YouTube player`}
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center">No songs found for this band.</p>
                  )}
                  <div className="col-md-4">
                    <div className="card mb-4 shadow-sm">
                      <div className="card-body">
                        <div className="card-title d-flex align-items-center flex-wrap">
                          <span className="order-circle">★</span>
                          <strong className="ms-2">Pad & Click</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="btn-group" />
                          <button className="btn btn-sm btn-outline-dark" onClick={() => setShowPracticeModal(true)}>
                            Open
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Modal show={showModal} onHide={closeChordModal} fullscreen={true}>
              <Modal.Body
                ref={scrollContainerRef}
                style={{
                  backgroundColor: "black",
                  color: "white",
                  overflow: "auto",
                }}
              >
                {selectedSong && (
                  <div>
                    <div
                      className="fixed-top p-3"
                      style={{
                        zIndex: 998,
                        background: "transparent",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "flex-start",
                        gap: "0.35em",
                        paddingRight: "1rem",
                      }}
                    >
                      <button id="chordDrawerToggle" className="btn btn-outline-light btn-sm" onClick={() => setChordDrawerOpen((open) => !open)} aria-label="Open controls">
                        ☰
                      </button>
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        aria-label="Close"
                        onClick={closeChordModal}
                        style={{
                          position: "absolute",
                          left: "10px",
                          top: "10px",
                        }}
                      />

                      {videoLoaded && hasOnlineYouTube(selectedSong) && selectedSongYouTubeId && (
                        <div id="yt-iframe" className="youtube-iframe" style={{ display: showVideo ? "block" : "none" }}>
                          <iframe src={`https://www.youtube.com/embed/${selectedSongYouTubeId}?autoplay=1`} frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                        </div>
                      )}
                      {/* Playback island */}
                      {canShowPlaybackIsland ? (
                        <div
                          style={{
                            position: "fixed",
                            top: "12px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1300,
                          }}
                        >
                          <div className="bg-dark text-light d-flex align-items-center gap-2 px-3 py-1 rounded-pill shadow" style={{ border: "1px solid rgba(255,255,255,0.2)" }}>
                            {hasPlayableStems ? (
                              <div className="d-flex align-items-center gap-2 me-1">
                                <span className="small text-light">Tracks</span>
                                <div className="form-check form-switch m-0">
                                  <input className="form-check-input" type="checkbox" role="switch" id="tracksToggle" checked={effectivePlaybackMode === "stems"} onChange={(e) => setPlaybackMode(e.target.checked ? "stems" : "lite")} />
                                </div>
                              </div>
                            ) : null}
                            <button
                              className={`btn btn-sm ${padPlayer.isPlaying ? "btn-warning" : "btn-outline-warning"} rounded-circle no-hover`}
                              onClick={handleTogglePad}
                              disabled={padPlayer.loading}
                              title={padPlayer.isPlaying ? "Stop pad" : "Play pad"}
                            >
                              {padPlayer.currentKey}
                            </button>
                            {effectivePlaybackMode === "lite" || effectivePlaybackMode === "stems" ? (
                              <button
                                className={`btn btn-sm ${clickTrack.isClickPlaying ? "btn-info" : "btn-outline-info"} rounded-circle no-hover`}
                                onClick={handleToggleClick}
                                title={clickTrack.isClickPlaying ? "Stop click" : "Start click"}
                                disabled={stemPlayer.loading}
                              >
                                ●
                              </button>
                            ) : null}
                            <button
                              className={`btn btn-sm ${guideState.isPlaying ? "btn-danger" : "btn-success"} rounded-circle no-hover`}
                              onClick={handleToggleGuide}
                              title={guideMissingMeasures ? "Measures required for guide" : guideState.isPlaying ? "Stop guide" : "Start guide"}
                              disabled={guideMissingMeasures}
                            >
                              {guideState.isPlaying ? "■" : "\u25B6"}
                            </button>
                            {hasPlayableStems && stemPlayer.loading ? (
                              <div className="d-flex align-items-center gap-1 text-light" title="Downloading tracks">
                                <div className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" />
                                <span className="small">Downloading</span>
                              </div>
                            ) : null}
                            <button className="btn btn-sm btn-outline-light rounded-circle" onClick={() => stemPlayer.setExpanded((v) => !v)} title="Mix / settings">
                              ⚙️
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {canShowPlaybackIsland && stemPlayer.isExpanded && (
                        <>
                          <div
                            onClick={() => stemPlayer.setExpanded(false)}
                            style={{
                              position: "fixed",
                              inset: 0,
                              zIndex: 1199,
                              background: "rgba(0,0,0,0.3)",
                            }}
                          />
                          <div
                            className="card bg-dark text-light"
                            style={{
                              position: "fixed",
                              top: "56px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: "calc(100vw - 32px)",
                              maxWidth: "1100px",
                              maxHeight: "70vh",
                              overflowY: "auto",
                              padding: "10px",
                              border: "1px solid rgba(255,255,255,0.1)",
                              zIndex: 1200,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <strong>Stem Mix</strong>
                                {stemPlayer.duration ? (
                                  <span className="small text-light">
                                    {formatTime(stemPlayer.progress || 0)} / {formatTime(stemPlayer.duration || 0)}
                                  </span>
                                ) : null}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {hasPlayableStems && stemPlayer.stemsPackageId ? (
                                  <button className="btn btn-sm btn-outline-light" onClick={stemPlayer.refreshStems} disabled={stemPlayer.isPlaying || stemPlayer.loading} title={stemPlayer.isPlaying ? "Stop stems to refresh" : "Refresh tracks"}>
                                    ⟳
                                  </button>
                                ) : null}
                                <button className="btn btn-sm btn-outline-light" onClick={refreshActiveData} disabled={isManualRefreshRunning} title="Refresh setlist and song data">
                                  {isManualRefreshRunning ? "Syncing..." : "Sync"}
                                </button>
                                <button className="btn btn-sm btn-outline-light" onClick={() => stemPlayer.setExpanded(false)}>
                                  ✕
                                </button>
                              </div>
                            </div>
                            {guideMissingMeasures ? <div className="small text-warning mb-2">This song needs measure counts before a guide can be generated.</div> : null}
                            {stemPlayer.duration && stemPlayer.stemsPackageId ? (
                              <div className="mb-3">
                                {stemPlayer.waveform?.length ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-end",
                                      gap: "1px",
                                      height: "60px",
                                      marginBottom: "6px",
                                    }}
                                  >
                                    {stemPlayer.waveform.map((v, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          width: "100%",
                                          height: `${Math.max(4, v * 60)}px`,
                                          background: stemPlayer.progress / Math.max(0.001, stemPlayer.duration) >= i / stemPlayer.waveform.length ? "#0dcaf0" : "rgba(255,255,255,0.35)",
                                          opacity: 0.9,
                                        }}
                                      />
                                    ))}
                                  </div>
                                ) : null}
                                <input type="range" min="0" max={Math.max(0.01, stemPlayer.duration)} step="0.01" className="form-range" value={stemPlayer.progress || 0} disabled />
                              </div>
                            ) : null}
                            {stemPlayer.error ? <div className="text-warning small mb-2">{stemPlayer.error}</div> : null}
                            <div className="d-grid" style={{ gap: "6px" }}>
                              <div className="p-2 bg-secondary bg-opacity-25 rounded">
                                <div className="d-flex align-items-center gap-2">
                                  <button className={`btn btn-sm ${padPlayer.isPlaying ? "btn-warning" : "btn-outline-light"} no-hover`} onClick={handleTogglePad} disabled={padPlayer.loading} title={padPlayer.isPlaying ? "Stop pad" : "Play pad"}>
                                    {padPlayer.currentKey}
                                  </button>
                                  <span className="small text-truncate" title="Pad" style={{ minWidth: "120px" }}>
                                    Pad
                                  </span>
                                  <input type="range" min="0" max="1.5" step="0.05" className="form-range flex-grow-1" value={Number(padPlayer.volume ?? 1)} onChange={(e) => padPlayer.setPadVolume(Number(e.target.value))} />
                                  <div className="btn-group btn-group-sm" role="group" aria-label="Pad Pan">
                                    {[
                                      { label: "L", val: -1 },
                                      { label: "C", val: 0 },
                                      { label: "R", val: 1 },
                                    ].map((opt) => (
                                      <button key={opt.label} className={`btn ${Math.round(padPlayer.pan || 0) === opt.val ? "btn-info" : "btn-outline-info"}`} onClick={() => padPlayer.setPadPan(opt.val)}>
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {effectivePlaybackMode === "lite" || effectivePlaybackMode === "stems" ? (
                                <div className="p-2 bg-secondary bg-opacity-25 rounded">
                                  <div className="d-flex align-items-center gap-2">
                                    <button className={`btn btn-sm ${clickTrack.isClickPlaying ? "btn-info" : "btn-outline-light"} no-hover`} onClick={handleToggleClick} title={clickTrack.isClickPlaying ? "Stop click" : "Play click"}>
                                      ●
                                    </button>
                                    <span className="small text-truncate" title="Click" style={{ minWidth: "120px" }}>
                                      Click
                                    </span>
                                    <input type="range" min="0" max="1.5" step="0.05" className="form-range flex-grow-1" value={Number(clickTrack.volume ?? 1)} onChange={(e) => clickTrack.setClickVolume(Number(e.target.value))} />
                                    <div className="btn-group btn-group-sm" role="group" aria-label="Click Pan">
                                      {[
                                        { label: "L", val: -1 },
                                        { label: "C", val: 0 },
                                        { label: "R", val: 1 },
                                      ].map((opt) => (
                                        <button key={opt.label} className={`btn ${Math.round(clickTrack.pan || 0) === opt.val ? "btn-info" : "btn-outline-info"}`} onClick={() => clickTrack.setClickPan(opt.val)}>
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              <div className="p-2 bg-secondary bg-opacity-25 rounded">
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    className={`btn btn-sm ${guideState.isPlaying ? "btn-danger" : "btn-outline-light"} no-hover`}
                                    onClick={handleToggleGuide}
                                    title={guideMissingMeasures ? "Measures required for guide" : guideState.isPlaying ? "Stop guide" : "Play guide"}
                                    disabled={guideMissingMeasures}
                                  >
                                    {guideState.isPlaying ? "■" : "\u25B6"}
                                  </button>
                                  <span className="small text-truncate" title="Guide" style={{ minWidth: "120px" }}>
                                    Guide
                                  </span>
                                  <input
                                    type="range"
                                    min="0"
                                    max="1.5"
                                    step="0.05"
                                    className="form-range flex-grow-1"
                                    value={Number(guideState.volume ?? 1)}
                                    onChange={(e) => setGuideState((prev) => ({ ...prev, volume: Number(e.target.value) }))}
                                  />
                                  <div className="btn-group btn-group-sm" role="group" aria-label="Guide Pan">
                                    {[
                                      { label: "L", val: -1 },
                                      { label: "C", val: 0 },
                                      { label: "R", val: 1 },
                                    ].map((opt) => (
                                      <button key={opt.label} className={`btn ${Math.round(guideState.pan || 0) === opt.val ? "btn-info" : "btn-outline-info"}`} onClick={() => setGuideState((prev) => ({ ...prev, pan: opt.val }))}>
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {hasPlayableStems && effectivePlaybackMode === "stems" && stemPlayer.stemsPackageId ? (
                                stemPlayer.tracks.length === 0 ? (
                                  <div className="small text-muted">No tracks found.</div>
                                ) : (
                                  stemPlayer.tracks
                                    .slice()
                                    .sort((a, b) => {
                                      const orderMap = { guide: 1, click: 2 };
                                      const aVal = orderMap[a.channel_role] || 99;
                                      const bVal = orderMap[b.channel_role] || 99;
                                      if (aVal !== bVal) return aVal - bVal;
                                      return (a.display_order ?? 0) - (b.display_order ?? 0);
                                    })
                                    .map((t) => (
                                      <div key={t.id} className="p-2 bg-secondary bg-opacity-25 rounded">
                                        <div className="d-flex align-items-center gap-2">
                                          <button className={`btn btn-sm ${t.muted ? "btn-outline-light" : "btn-light"}`} onClick={() => stemPlayer.setTrackMute(t.id, !t.muted)} title={t.muted ? "Unmute" : "Mute"}>
                                            {t.muted ? "🔇" : "🔊"}
                                          </button>
                                          <span className="small text-truncate" title={t.name} style={{ minWidth: "80px" }}>
                                            {t.name}
                                          </span>
                                          <input type="range" min="0" max="1.5" step="0.05" className="form-range flex-grow-1" value={Number(t.volume_default ?? 1)} onChange={(e) => stemPlayer.setTrackVolume(t.id, Number(e.target.value))} />
                                          <div className="btn-group btn-group-sm" role="group" aria-label="Pan">
                                            {[
                                              { label: "L", val: -1 },
                                              { label: "C", val: 0 },
                                              { label: "R", val: 1 },
                                            ].map((opt) => (
                                              <button key={opt.label} className={`btn ${Math.round((t.pan_default ?? 0) / 100) === opt.val ? "btn-info" : "btn-outline-info"}`} onClick={() => stemPlayer.setTrackPan(t.id, opt.val)}>
                                                {opt.label}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                )
                              ) : null}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="chordpro-wrapper" style={{ position: "relative" }}>
                      {/* Canvas overlay for user notes (unchanged) */}
                      {notesVisibleOn ? (
                        <canvas
                          ref={canvasRef}
                          className={`notes-canvas ${isEditing ? "editing" : ""}`}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: 999,
                            pointerEvents: isEditing ? "auto" : "none",
                            touchAction: isEditing ? "none" : "auto",
                          }}
                          onMouseDown={startDraw}
                          onMouseMove={draw}
                          onMouseUp={endDraw}
                          onMouseLeave={endDraw}
                          onTouchStart={startDraw}
                          onTouchMove={draw}
                          onTouchEnd={endDraw}
                        />
                      ) : null}

                      {/* Song order (unchanged) */}
                      <span className="order-circle" style={{ marginRight: "auto", marginLeft: "26px" }}>
                        {selectedSong?.order}
                      </span>

                      {/* ======= Metadata / Title area ======= */}
                      {(() => {
                        // compute directives from chordpro (or fallback to songs table)
                        const chordSource = selectedSong?.chordpro || "";
                        const directives = extractDirectives(chordSource);

                        // Prefer directive title, then selectedSong.title
                        const displayTitle = directives.title || selectedSong?.title || "";
                        // Prefer directive time, then nothing
                        const displayTime = directives.time || "";
                        // Use the current transposed key from the dropdown (chordKey state)
                        const displayKey = chordKey || selectedSong?.skey || "";
                        const displayTempo = directives.tempo ? `♩=${directives.tempo}` : "";
                        const displayDuration = directives.duration ? `dur: ${directives.duration}` : "";

                        return (
                          <div
                            className="chordpro-metadata"
                            style={{
                              color: "white",
                              padding: "16px 15px 0px 20px",
                              zIndex: 995,
                            }}
                          >
                            {/* Title (matches chordsheetjs output class) */}
                            {displayTitle ? (
                              <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
                                <h1 className="title" style={{ margin: 0, fontSize: "1.6rem", lineHeight: 1 }}>
                                  {displayTitle}
                                </h1>

                                {/* small right-aligned meta (time signature) */}
                                {displayTime ? (
                                  <small className="time-signature" style={{ marginLeft: "0.5rem", opacity: 0.85, fontSize: "0.9rem" }} aria-label={`Time signature ${displayTime}`}>
                                    {displayTime}
                                  </small>
                                ) : null}

                                {/* optional small meta elements */}

                                {displayKey ? <small className="song-key">Key: {displayKey}</small> : null}
                                {displayTempo ? <small className="song-tempo">{displayTempo}</small> : null}
                                {displayDuration ? <small className="song-duration">{displayDuration}</small> : null}
                              </div>
                            ) : (
                              /* If no title available, still show small meta row */
                              <div style={{ display: "block", justifyContent: "flex-end", gap: "0.6rem" }}>
                                {displayTime ? <small className="text-muted">{displayTime}</small> : null}
                                {displayKey ? <small className="text-muted">Key: {displayKey}</small> : null}
                                {displayTempo ? <small className="text-muted">{displayTempo}bpm</small> : null}
                                {displayDuration ? <small className="text-muted">{displayDuration}</small> : null}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ======= The main transposed content (sections or full) ======= */}
                      <div className="chordpro-content" style={{ fontSize: `${fontSize}px`, padding: "5px", paddingTop: 8 }}>
                        {renderedSections && !useMonolithicView
                          ? // === Case 1: Render from sections (when toggle is OFF) ===
                            renderedSections
                          : // === Case 2: Monolithic chordpro (when toggle is ON or no sections) ===
                            (() => {
                              return <div className="chordpro-full" dangerouslySetInnerHTML={{ __html: transposedHtml }} />;
                            })()}
                      </div>

                      {/* Error fallback: show if nothing rendered */}
                      {!sections?.length && !selectedSong?.chordpro && <div className="chordpro-error">Error: No content rendered.</div>}
                    </div>
                    <div
                      className="fixed-bottom p-3"
                      style={{
                        zIndex: 1001,
                        background: "transparent",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {isPresenceOnline && visibleFollowLeader ? (
                        <button
                          id="followLeaderButton"
                          className="btn btn-outline-light btn-sm mr-2"
                          onClick={() => {
                            const leaderId = String(visibleFollowLeader.leader_user_id);
                            if (String(selectedLeaderId || "") === leaderId) {
                              setSelectedLeaderId(null);
                            } else {
                              setSelectedLeaderId(leaderId);
                            }
                          }}
                        >
                          {visibleFollowLeader.leader_username || "Leader"}
                        </button>
                      ) : null}
                      <button className="btn btn-success btn-sm mr-2 startscroll" onClick={startScroll}>
                        ►
                      </button>
                      <button className="btn btn-danger btn-sm mr-2 stopscroll" onClick={stopScroll} style={{ display: "none" }}>
                        ■
                      </button>
                      {/* Notes controls */}
                      {notesVisibleOn ? (
                        <>
                          <button id="editNotesButton" className={`btn btn-${isEditing ? "warning" : "outline-light"} btn-sm mr-2`} onClick={toggleEditMode}>
                            {isEditing ? "✅" : "✏️"}
                          </button>

                          <button
                            id="clearNotesButton"
                            className="btn btn-outline-danger btn-sm mr-2"
                            onClick={() => {
                              if (!window.confirm("Clear saved notes for this song?")) return;
                              clearNotesForSong();
                            }}
                          >
                            🗑️
                          </button>
                        </>
                      ) : null}

                      <button id="toggleNavButton" onClick={toggleNav} className="btn btn-light mr-2" aria-label="Toggle song navigation">
                        <h3 className="p-0">♫</h3>
                      </button>
                      <button id="nextbutton" className="btn btn-light" onClick={handleNextSong}>
                        <h3 className="p-0">⏩</h3>
                      </button>
                    </div>
                    {chordDrawerOpen ? <div className="chord-drawer-backdrop" onClick={() => setChordDrawerOpen(false)} /> : null}
                    <aside className={`chord-drawer ${chordDrawerOpen ? "open" : ""}`}>
                      <div className="chord-drawer-header">
                        <div className="chord-drawer-title">Controls</div>
                        <button className="chord-drawer-close" onClick={() => setChordDrawerOpen(false)} aria-label="Close controls">
                          ×
                        </button>
                      </div>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">Follow</div>
                        {isPresenceOnline && activeLeaders.length === 0 ? (
                          <button className="chord-drawer-btn" onClick={() => requestLeaderPresence?.({ force: true })}>
                            Scan for Leader
                          </button>
                        ) : (
                          <div className="chord-drawer-buttons" style={{ gridTemplateColumns: "1fr" }}>
                            {activeLeaders.map((leader) => {
                              const leaderId = String(leader.leader_user_id);
                              const isActive = String(selectedLeaderId || "") === leaderId;
                              return (
                                <button key={leaderId} className="chord-drawer-btn" onClick={() => (isActive ? setSelectedLeaderId(null) : setSelectedLeaderId(leaderId))}>
                                  {leader.leader_username || "Leader"}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </section>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">View</div>
                        <div className="d-flex flex-column gap-3">
                          <div className="d-flex align-items-center justify-content-between gap-3">
                            <span>Tracks</span>
                            <div className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={tracksEnabled}
                                onChange={async (e) => {
                                  const nextValue = e.target.checked;
                                  setTracksEnabled(nextValue);
                                  try {
                                    await savePreference("tracksEnabled", nextValue);
                                  } catch (_) {
                                    // ignore preference failures
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between gap-3">
                            <span>Notes</span>
                            <div className="form-check form-switch m-0">
                              <input className="form-check-input" type="checkbox" role="switch" checked={notesVisibleOn} onChange={(e) => setNotesVisibleOn(e.target.checked)} />
                            </div>
                          </div>
                        </div>
                      </section>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">Key</div>
                        <select className="chord-drawer-select" value={chordKey} onChange={handleKeyChange}>
                          {["Ab", "A", "Bb", "B", "C", "C#", "Db", "D", "Eb", "E", "F", "F#", "Gb", "G", "Am", "A#m", "Bbm", "Bm", "Cm", "C#m", "Dbm", "Dm", "D#m", "Ebm", "Em", "Fm", "F#m", "Gbm", "Gm", "G#m", "Abm"].map((key) => (
                            <option key={key} value={key}>
                              {key}
                            </option>
                          ))}
                        </select>
                        <div className="chord-drawer-buttons">
                          <button className="chord-drawer-btn" onClick={() => handleTextSize(false)}>
                            A-
                          </button>
                          <button className="chord-drawer-btn" onClick={() => handleTextSize(true)}>
                            A+
                          </button>
                        </div>
                      </section>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">Pad</div>
                        <select
                          className="chord-drawer-select"
                          value={padKeyOverride || ""}
                          onChange={async (e) => {
                            const nextValue = e.target.value;
                            if (!nextValue) {
                              setPadKeyOverride(null);
                              if (padPrefKey) {
                                await savePreference(padPrefKey, { key: null, chordKey });
                              }
                              return;
                            }
                            setPadKeyOverride(nextValue);
                            if (padPrefKey) {
                              await savePreference(padPrefKey, { key: nextValue, chordKey });
                            }
                          }}
                        >
                          <option value="">{`Song Key (${chordKey || selectedSong?.skey || "C"})`}</option>
                          <optgroup label="Major">
                            {PAD_MAJOR_KEYS.map((key) => (
                              <option key={key} value={key}>
                                {key}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Minor">
                            {PAD_MINOR_KEYS.map((key) => (
                              <option key={key} value={key}>
                                {key}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </section>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">Click</div>
                        <select
                          className="chord-drawer-select"
                          value={clickPreset}
                          onChange={(e) => {
                            const preset = e.target.value;
                            setClickPreset(preset);
                            writeStoredClickPreset(preset);
                          }}
                        >
                          {Object.entries(CLICK_PRESETS).map(([presetId, preset]) => (
                            <option key={presetId} value={presetId}>
                              {preset.label}
                            </option>
                          ))}
                        </select>
                      </section>

                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">YouTube</div>
                        <button id="ytbutton" className="chord-drawer-btn" onClick={handleChordDrawerYoutubeToggle} disabled={!hasOnlineYouTube(selectedSong)}>
                          {showVideo ? "Hide YouTube" : "Show YouTube"}
                        </button>
                      </section>
                      <section className="chord-drawer-section">
                        <div className="chord-drawer-label">Sync</div>
                        <div className="chord-drawer-buttons" style={{ gridTemplateColumns: "1fr" }}>
                          <button className="chord-drawer-btn" onClick={refreshActiveData} disabled={isManualRefreshRunning}>
                            {isManualRefreshRunning ? "Refreshing..." : "Refresh from server"}
                          </button>
                        </div>
                      </section>

                      {isLeader ? (
                        <section className="chord-drawer-section">
                          <div className="chord-drawer-label">Leader</div>
                          <div className="chord-drawer-buttons">
                            <button className="chord-drawer-btn" onClick={() => openSongForm(selectedSong)}>
                              Edit Song
                            </button>
                            <button className="chord-drawer-btn" onClick={openArrangementEditor}>
                              Edit Arrangement
                            </button>
                            {availableSections && availableSections.length > 0 ? (
                              <button
                                className="chord-drawer-btn"
                                onClick={async () => {
                                  const newValue = !useMonolithicView;
                                  setUseMonolithicView(newValue);
                                  await savePreference("useMonolithicView", newValue);
                                }}
                              >
                                {useMonolithicView ? "Use Sections" : "Use ChordPro"}
                              </button>
                            ) : null}
                          </div>
                        </section>
                      ) : null}
                    </aside>
                    <div className="nav nav-pills nav-fill bg-light fixed-bottom" style={{ display: "none" }} id="songnav">
                      {songs.map((song) => {
                        // Normalize both IDs to strings for comparison
                        const songId = String(song.songid || song.id || song.ID);
                        const selectedSongId = String(selectedSong?.songid || selectedSong?.id || selectedSong?.ID);
                        const isActive = songId === selectedSongId;

                        return (
                          <li className="nav-item" key={songId}>
                            <button
                              className={`nav-link ${isActive ? "active" : ""}`}
                              onClick={() => {
                                openChordModal(song);
                                toggleNav();
                              }}
                            >
                              {song.title} {song.skey ? `(${song.skey})` : ""}
                            </button>
                          </li>
                        );
                      })}
                      <li className="nav-item">
                        <button
                          className="nav-link"
                          onClick={() => {
                            toggleNav();
                            openPracticeFromChord();
                          }}
                        >
                          Pad & Click
                        </button>
                      </li>
                      <button className="btn btn-outline-primary" onClick={closeChordModal} aria-label="Close modal">
                        <h4 className="p-0">▤</h4>
                      </button>
                      <button className="btn btn-outline-primary" onClick={() => window.location.reload()} aria-label="Refresh page">
                        <h4 className="p-0">↺</h4>
                      </button>
                      <span
                        className="badge rounded-pill bg-danger text-white"
                        style={{
                          margin: "10px 5px",
                          padding: "8px 12px",
                          zIndex: 1002,
                        }}
                        onClick={toggleNav}
                        aria-label="Close navigation"
                      >
                        <strong>×</strong>
                      </span>
                    </div>
                  </div>
                )}
              </Modal.Body>
            </Modal>
            <Modal show={showPracticeModal} onHide={closePracticeModal} fullscreen={true}>
              <Modal.Body
                style={{
                  backgroundColor: "black",
                  color: "white",
                  overflow: "auto",
                }}
              >
                <div id="practiceTopBar" className="fixed-top p-3" style={{ zIndex: 998, background: "transparent", display: "flex", justifyContent: "flex-start", alignItems: "flex-start" }}>
                  <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={closePracticeModal} />
                </div>
                <div style={{ paddingTop: "64px", paddingBottom: "32px" }}>
                  <div className="d-flex flex-column flex-lg-row align-items-lg-start gap-4">
                    <div className="flex-grow-1 p-3 bg-secondary bg-opacity-25 rounded">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <strong>Pad</strong>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <button
                          className={`btn btn-lg ${practicePadPlayer.isPlaying ? "btn-warning" : "btn-outline-warning"} rounded-circle no-hover`}
                          onClick={handlePracticeTogglePad}
                          disabled={practicePadPlayer.loading}
                          title={practicePadPlayer.isPlaying ? "Stop pad" : "Play pad"}
                          style={{ width: "68px", height: "68px" }}
                        >
                          {practicePadPlayer.currentKey}
                        </button>
                        <div style={{ minWidth: "200px" }}>
                          <label className="form-label small text-light">Pad Key</label>
                          <select className="form-select form-select-sm" value={practicePadKey} onChange={(e) => setPracticePadKey(e.target.value)}>
                            <optgroup label="Major">
                              {PAD_MAJOR_KEYS.map((key) => (
                                <option key={key} value={key}>
                                  {key}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Minor">
                              {PAD_MINOR_KEYS.map((key) => (
                                <option key={key} value={key}>
                                  {key}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-3">
                          <span className="small text-truncate" style={{ minWidth: "80px" }}>
                            Volume
                          </span>
                          <input type="range" min="0" max="1.5" step="0.05" className="form-range flex-grow-1" value={Number(practicePadVolume ?? 1)} onChange={(e) => setPracticePadVolume(Number(e.target.value))} />
                          {isLeader ? (
                            <div className="btn-group btn-group-sm" role="group" aria-label="Pad Pan">
                              {[
                                { label: "L", val: -1 },
                                { label: "C", val: 0 },
                                { label: "R", val: 1 },
                              ].map((opt) => (
                                <button key={`pad-${opt.label}`} className={`btn ${Math.round(practicePadPan || 0) === opt.val ? "btn-warning" : "btn-outline-warning"}`} onClick={() => setPracticePadPan(opt.val)}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1 p-3 bg-secondary bg-opacity-25 rounded">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <strong>Click</strong>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <button
                          className={`btn btn-lg ${practiceClickTrack.isClickPlaying ? "btn-danger" : "btn-info"} rounded-circle no-hover`}
                          onClick={handlePracticeToggleClick}
                          title={practiceClickTrack.isClickPlaying ? "Stop click" : "Start click"}
                          style={{ width: "68px", height: "68px" }}
                        >
                          {practiceClickTrack.isClickPlaying ? "■" : "\u25B6"}
                        </button>
                        <div className="d-flex align-items-end gap-3 flex-wrap">
                          <div>
                            <label className="form-label small text-light">Tempo</label>
                            <input type="number" className="form-control form-control-sm" min="30" max="240" step="1" value={practiceTempo} onChange={(e) => setPracticeTempo(Number(e.target.value) || 0)} style={{ maxWidth: "110px" }} />
                          </div>
                          <div>
                            <label className="form-label small text-light">Time</label>
                            <select className="form-select form-select-sm" value={practiceBeatsPerMeasure} onChange={(e) => setPracticeBeatsPerMeasure(Number(e.target.value))}>
                              {[
                                { val: 4, label: "4/4" },
                                { val: 6, label: "6/8" },
                                { val: 3, label: "3/4" },
                                { val: 12, label: "12/8" },
                                { val: 2, label: "2/4" },
                                { val: 5, label: "5/4" },
                                { val: 7, label: "7/4" },
                                { val: 8, label: "8/4" },
                                { val: 9, label: "9/4" },
                              ].map((opt) => (
                                <option key={opt.label} value={opt.val}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 d-grid" style={{ gap: "10px" }}>
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <span className="small text-truncate">Downbeat</span>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" role="switch" checked={!practiceSuppressDownbeat} onChange={(e) => setPracticeSuppressDownbeat(!e.target.checked)} />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className="small text-truncate" style={{ minWidth: "80px" }}>
                            Volume
                          </span>
                          <input type="range" min="0" max="1.5" step="0.05" className="form-range flex-grow-1" value={Number(practiceClickVolume ?? 1)} onChange={(e) => setPracticeClickVolume(Number(e.target.value))} />
                          {isLeader ? (
                            <div className="btn-group btn-group-sm" role="group" aria-label="Click Pan">
                              {[
                                { label: "L", val: -1 },
                                { label: "C", val: 0 },
                                { label: "R", val: 1 },
                              ].map((opt) => (
                                <button key={`click-${opt.label}`} className={`btn ${Math.round(practiceClickPan || 0) === opt.val ? "btn-info" : "btn-outline-info"}`} onClick={() => setPracticeClickPan(opt.val)}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <section className="mt-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <strong>Song Presets</strong>
                      <span className="small text-white-50">Current service</span>
                    </div>
                    {songs.length ? (
                      <div className="row g-3">
                        {songs.map((song) => {
                          const preset = getPracticeSongPreset(song);
                          const songId = song.ss_id || song.songid || song.id;
                          const isSelected = String(selectedPracticeSongId || "") === String(songId || "");
                          return (
                            <div className="col-12 col-md-6 col-xl-4" key={songId}>
                              <button
                                type="button"
                                className={`w-100 h-100 text-start border rounded p-3 ${isSelected ? "border-info bg-info bg-opacity-25" : "border-secondary bg-secondary bg-opacity-25"}`}
                                style={{ color: "white" }}
                                onClick={() => handlePracticeSongPreset(song)}
                              >
                                <div className="fw-semibold text-truncate mb-3">{song.title || "Untitled song"}</div>
                                <div className="d-flex justify-content-between gap-3 small">
                                  <span className="text-white-50">Key</span>
                                  <span>{preset.key || "Not found"}</span>
                                </div>
                                <div className="d-flex justify-content-between gap-3 small mt-1">
                                  <span className="text-white-50">Time</span>
                                  <span>{preset.timeSignature || "Not found"}</span>
                                </div>
                                <div className="d-flex justify-content-between gap-3 small mt-1">
                                  <span className="text-white-50">Tempo</span>
                                  <span>{preset.bpm ? `${preset.bpm} BPM` : "Not found"}</span>
                                </div>
                                {preset.missing.length ? <div className="small text-warning mt-3">Missing {preset.missing.join(", ")}; add it to this song before using this preset.</div> : null}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-white-50">No songs are in the current service.</div>
                    )}
                  </section>
                </div>
                {practiceNavOpen ? (
                  <div id="practiceNav" className="nav nav-pills nav-fill bg-light fixed-bottom" style={{ zIndex: 1002 }}>
                    {songs.map((song) => {
                      return (
                        <li className="nav-item" key={song.id}>
                          <button
                            className="nav-link"
                            onClick={() => {
                              openSongFromPractice(song);
                              setPracticeNavOpen(false);
                            }}
                          >
                            {song.title} {song.skey ? `(${song.skey})` : ""}
                          </button>
                        </li>
                      );
                    })}
                    <button className="btn btn-outline-primary" onClick={closePracticeModal} aria-label="Close modal">
                      <h4 className="p-0">▤</h4>
                    </button>
                    <button className="btn btn-outline-primary" onClick={() => window.location.reload()} aria-label="Refresh page">
                      <h4 className="p-0">↺</h4>
                    </button>
                    <span
                      className="badge rounded-pill bg-danger text-white"
                      style={{
                        margin: "10px 5px",
                        padding: "8px 12px",
                        zIndex: 1002,
                      }}
                      onClick={() => setPracticeNavOpen(false)}
                      aria-label="Close navigation"
                    >
                      <strong>×</strong>
                    </span>
                  </div>
                ) : null}
                <div
                  className="fixed-bottom p-3"
                  style={{
                    zIndex: 1001,
                    background: "transparent",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <button className="btn btn-light mr-2" onClick={togglePracticeNav} aria-label="Toggle song navigation">
                    <h3 className="p-0">♫</h3>
                  </button>
                </div>
              </Modal.Body>
            </Modal>
            <Modal show={showSongForm} onHide={() => setShowSongForm(false)} size="lg">
              <Modal.Header closeButton className="d-flex justify-content-between align-items-center song-form-header">
                <Modal.Title className="mb-0">{editingSong ? "Edit Song" : "Add New Song"}</Modal.Title>
                <Button variant="primary" size="sm" type="submit" form="editsongForm" className="song-form-save-top">
                  💾 Save
                </Button>
              </Modal.Header>

              <Modal.Body className="song-form-body">
                <Form onSubmit={handleSongSubmit} id={"editsongForm"} className="song-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.title}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          title: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Key</Form.Label>
                    <Form.Select
                      value={songFormData.skey}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          skey: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select Key</option>
                      {["Ab", "A", "Bb", "B", "C", "C#", "Db", "D", "Eb", "E", "F", "F#", "Gb", "G", "G#m", "Am", "Bbm", "Bm", "Cm", "C#m", "Dbm", "Dm", "Ebm", "Em", "Fm", "F#m", "Gbm", "Gm"].map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="row g-3 align-items-end">
                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>YouTube ID (e.g., cOfXB-QYXMM)</Form.Label>
                      <Form.Control
                        type="text"
                        value={songFormData.url}
                        onChange={(e) =>
                          setSongFormData({
                            ...songFormData,
                            url: e.target.value,
                          })
                        }
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Track YouTube ID (defaults to YouTube ID)</Form.Label>
                      <Form.Control
                        type="text"
                        value={songFormData.track}
                        onChange={(e) =>
                          setSongFormData({
                            ...songFormData,
                            track: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Spotify Link (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={songFormData.spotify}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          spotify: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Label className="mb-0">ChordPro (lyrics and chords)</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => openSectionEditor(editingSong?.id || editingSong?.songid || editingSong?.ID)} disabled={!(editingSong?.id || editingSong?.songid || editingSong?.ID)}>
                          {availableSections && availableSections.length > 0 ? "Edit Sections" : "Generate Sections"}
                        </Button>
                        {(editingSong?.id || editingSong?.songid || editingSong?.ID) && (
                          <Button variant="danger" size="sm" onClick={handleDeleteSectionsAndArrangements}>
                            Delete Sections & Arrangements
                          </Button>
                        )}
                      </div>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={15} // Large for memo-style input
                      value={songFormData.chordpro}
                      onChange={(e) =>
                        setSongFormData({
                          ...songFormData,
                          chordpro: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <div className="song-form-footer">
                    <Button variant="primary" type="submit" className="song-form-save-bottom">
                      💾 Save
                    </Button>
                  </div>
                </Form>
              </Modal.Body>
            </Modal>

            {/* Arrangement Editor Modal */}
            <Modal show={showArrangementEditor} onHide={() => setShowArrangementEditor(false)} size="lg" fullscreen="md-down" className="arrange-modal">
              <Modal.Header closeButton className="arrange-modal-header">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-between">
                  <Modal.Title className="mb-0">Arrange: {selectedSong?.title}</Modal.Title>
                  <Button variant="light" size="sm" onClick={() => openSectionEditor(selectedSong?.songid || selectedSong?.id || selectedSong?.ID)}>
                    Edit Section Text
                  </Button>
                </div>
              </Modal.Header>
              <Modal.Body className="arrange-modal-body">
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;

                    const items = Array.from(sections);
                    const [reordered] = items.splice(result.source.index, 1);
                    items.splice(result.destination.index, 0, reordered);

                    setSections(items);
                    saveArrangementOrder(items);
                  }}
                >
                  <Droppable droppableId="arrangement">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} style={{ minHeight: "200px" }}>
                        {/* Add button at the very top */}
                        <div className="text-center mb-2">
                          <DropdownButton size="sm" variant="success" title="+ Add Section">
                            {availableSections.map((section) => (
                              <Dropdown.Item key={`top-${section.id}`} onClick={() => addSectionToArrangement(section.id, 0)}>
                                <strong>{section.section_label}</strong>
                                <div className="text-muted small">{section.content.substring(0, 40)}...</div>
                              </Dropdown.Item>
                            ))}
                          </DropdownButton>
                        </div>

                        {sections.map((section, index) => (
                          <div key={`arr-${section.arrangement_section_id}-wrapper`}>
                            <Draggable key={`arr-${section.arrangement_section_id}`} draggableId={`arr-${section.arrangement_section_id}`} index={index}>
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="d-flex align-items-center gap-2 mb-2 p-2 bg-light border rounded">
                                  <span className="drag-handle" style={{ cursor: "grab" }}>
                                    ☰
                                  </span>
                                  <div className="flex-grow-1">
                                    <strong>{section.section_label}</strong>
                                    <div className="text-muted small" style={{ maxHeight: "3em", overflow: "hidden" }}>
                                      {section.content.substring(0, 100)}...
                                    </div>
                                  </div>
                                  <button className="btn btn-sm btn-danger" onClick={() => removeSectionFromArrangement(section.arrangement_section_id)} title="Remove section">
                                    ×
                                  </button>
                                </div>
                              )}
                            </Draggable>

                            {/* Add button after each section */}
                            <div className="text-center mb-2">
                              <DropdownButton size="sm" variant="outline-success" title="+" style={{ fontSize: "0.75rem" }}>
                                {availableSections.map((availSection) => (
                                  <Dropdown.Item key={`after-${index}-${availSection.id}`} onClick={() => addSectionToArrangement(availSection.id, index + 1)}>
                                    <strong>{availSection.section_label}</strong>
                                    <div className="text-muted small">{availSection.content.substring(0, 40)}...</div>
                                  </Dropdown.Item>
                                ))}
                              </DropdownButton>
                            </div>
                          </div>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Modal.Body>
              <Modal.Footer className="arrange-modal-footer">
                <Button variant="secondary" onClick={() => setShowArrangementEditor(false)} className="arrange-close-btn">
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </div>
      <SectionEditorModal songId={sectionEditorSongId} bandId={sectionEditorBandId} isOpen={showSectionEditor} onClose={closeSectionEditor} onUpdated={(updated) => refreshSectionsAndArrangement(sectionEditorSongId, updated)} />
      {/* Global error toast - appears above all modals */}
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999 }}>
        <Toast onClose={() => setError("")} delay={5000} autohide show={!!error} bg="danger">
          <Toast.Header closeButton={true}>
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}

export default App;

function formatTime(sec = 0) {
  const minutes = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(sec % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
}
